<template>
  <div>
    <kpi-view-tabs current="kpi-tree" />

    <!-- A BARRA DE FILTROS, numa linha: periodo, PN e as tres acoes.
         Periodo e PN ocupavam metade da largura cada um, com os botoes numa
         segunda linha so deles. E nada aqui precisa mais de "Pesquisar": o
         periodo busca ao escolher (atalho, ‹ › ou calendario fechado com a faixa
         completa), e os campos que vao ao servidor buscam quando o dropdown
         fecha. O botao virou "Atualizar" -- recarregar o dia, que continua
         mudando enquanto a competencia esta aberta. -->
    <b-card
      no-body
      class="kpi-filters"
    >
      <b-card-body>
        <div class="kpi-bar">
          <div class="kpi-period">
            <b-button
              v-b-tooltip.hover.top="'Mês anterior'"
              variant="outline-secondary"
              class="btn-icon kpi-period__step"
              aria-label="Mês anterior"
              :disabled="loading"
              @click="andarMes(-1)"
            >
              <feather-icon
                icon="ChevronLeftIcon"
                size="16"
              />
            </b-button>
            <div class="kpi-period__field">
              <feather-icon
                icon="CalendarIcon"
                size="16"
                class="kpi-period__icon"
              />
              <flat-pickr
                id="treeReportRange"
                v-model="rangeDate"
                class="form-control"
                :config="flatPickrConfig"
                placeholder="Escolha o período"
                aria-label="Período"
                @on-close="aoFecharCalendario"
              />
              <span class="kpi-period__tag">{{ rotuloDoPeriodo }}</span>
            </div>
            <b-button
              v-b-tooltip.hover.top="'Próximo mês'"
              variant="outline-secondary"
              class="btn-icon kpi-period__step"
              aria-label="Próximo mês"
              :disabled="loading || !temProximoMes"
              @click="andarMes(1)"
            >
              <feather-icon
                icon="ChevronRightIcon"
                size="16"
              />
            </b-button>
          </div>

          <v-select
            id="treeConsultant"
            ref="selPn"
            v-model="consultant"
            class="kpi-bar__pn"
            :options="consultants"
            :loading="loadingConsultants"
            :close-on-select="false"
            label="name"
            multiple
            placeholder="Todos os PNs"
            @close="buscarSeMudou"
            @option:deselected="buscarSeFechado('selPn')"
          >
            <!-- O v-select não dá nome acessível ao campo de busca, e havia
                 navegador mostrando o aria-label no lugar do placeholder: sem
                 ele, os seis selects da tela apareciam como "null". -->
            <template #search="{ attributes, events }">
              <input
                class="vs__search"
                v-bind="attributes"
                :aria-label="attributes.placeholder || 'PN'"
                v-on="events"
              >
            </template>
            <template #no-options>
              Sem resultados
            </template>
          </v-select>

          <b-button
            :variant="showMatrixPanel || chipsDeRecorte.length ? 'outline-primary' : 'outline-secondary'"
            :aria-expanded="showMatrixPanel ? 'true' : 'false'"
            aria-controls="kpiRecorte"
            @click.prevent="showMatrixPanel = !showMatrixPanel"
          >
            <feather-icon
              icon="FilterIcon"
              class="mr-50"
            />
            <span>Recorte</span>
            <b-badge
              v-if="chipsDeRecorte.length"
              pill
              variant="primary"
              class="ml-50"
            >{{ chipsDeRecorte.length }}</b-badge>
          </b-button>
          <b-button
            variant="outline-secondary"
            @click.prevent="showColumnsPanel = !showColumnsPanel"
          >
            <feather-icon
              icon="ColumnsIcon"
              class="mr-50"
            />
            <span>Componentes</span>
            <b-badge
              pill
              variant="light-secondary"
              class="ml-50"
            >{{ selectedColumnKeys.length }}</b-badge>
          </b-button>
          <b-button
            variant="outline-primary"
            class="kpi-bar__refresh"
            :disabled="loading"
            @click.prevent="search"
          >
            <b-spinner
              v-if="loading"
              small
              class="mr-50"
            />
            <feather-icon
              v-else
              icon="RefreshCwIcon"
              class="mr-50"
            />
            <span>{{ loading ? 'Atualizando…' : 'Atualizar' }}</span>
          </b-button>
        </div>

        <!-- A FILEIRA DE RECORTE ATIVO.
             Antes daqui, o unico sinal de que havia filtro ligado era um botao
             mudando de cor -- e o painel que guarda os campos fica fechado. Ou
             seja: dava para olhar a tela inteira, ver um numero menor do que se
             esperava, e nao ter como saber por que. Cada chip nomeia UM recorte
             e o desliga no x, sem abrir painel nenhum.

             Os de admin entram aqui tambem. "Brutos" e "Materializado" mudam
             TODOS os numeros da tela, e moravam numa gaveta que, fechada, nao
             deixava rastro nenhum. -->
        <div
          v-if="chipsDeRecorte.length"
          class="kpi-chips"
        >
          <span
            v-for="chip in chipsDeRecorte"
            :key="chip.key"
            class="kpi-chip"
          >
            <span class="kpi-chip__k">{{ chip.rotulo }}</span>
            {{ chip.valor }}
            <button
              type="button"
              class="kpi-chip__x"
              :aria-label="`Remover filtro ${chip.rotulo}`"
              @click="limparChip(chip)"
            >
              <feather-icon
                icon="XIcon"
                size="11"
              />
            </button>
          </span>
          <button
            type="button"
            class="kpi-chips__clear"
            @click="limparRecorte"
          >
            limpar filtros
          </button>
          <feather-icon
            v-b-tooltip.hover.left="'O mesmo recorte alimenta totalizadores, árvore e detalhamento.'"
            icon="InfoIcon"
            size="14"
            class="kpi-chips__info"
          />
        </div>

        <!-- O RECORTE: so o que muda os NUMEROS.
             Tres grupos lado a lado, cada um com titulo e filete -- a mesma
             linguagem das faixas da tabela. Eram quatro secoes empilhadas em
             linhas de quatro colunas, com metade das celulas vazias, e depois
             quatro caixas verticais com fundos desiguais.

             Sairam daqui: "Nome" (virou o Localizar da tabela, que acha sem
             tirar ninguem da soma), "Ate o nivel" (o "Abrir ate" da tabela ja
             faz a leitura por nivel, e este cortava os niveis de baixo do
             TOTAL), e "So com movimento" e "Faturamento minimo", que so escondem
             linha e foram para o menu Exibicao da tabela. -->
        <b-collapse
          id="kpiRecorte"
          v-model="showMatrixPanel"
        >
          <div class="kpi-recorte">
            <div class="kpi-recorte__grupos">
              <section class="kpi-recorte__grupo">
                <h6 class="kpi-recorte__titulo">
                  <feather-icon
                    icon="UsersIcon"
                    size="13"
                  />
                  Pessoas
                </h6>
                <div class="kpi-recorte__campos">
                  <div class="kpi-recorte__campo">
                    <label for="matrixPerspective">
                      Atribuição
                      <feather-icon
                        v-b-tooltip.hover.top="'Quem conta na linha: o dono da produção (quem vendeu) ou o especialista (quem executou). As duas leituras nunca se somam.'"
                        icon="InfoIcon"
                        size="13"
                        class="kpi-recorte__info"
                      />
                    </label>
                    <b-form-select
                      id="matrixPerspective"
                      :value="perspective"
                      :options="perspectiveOptions"
                      @change="changePerspective"
                    />
                  </div>
                  <div class="kpi-recorte__campo">
                    <label for="matrixRoles">Cargo</label>
                    <v-select
                      id="matrixRoles"
                      v-model="criteria.roleIds"
                      :options="matrixOptions.roles"
                      :reduce="option => option.id"
                      label="name"
                      multiple
                      :close-on-select="false"
                      placeholder="Todos os cargos"
                    >
                      <template #search="{ attributes, events }">
                        <input
                          class="vs__search"
                          v-bind="attributes"
                          :aria-label="attributes.placeholder || 'Cargo'"
                          v-on="events"
                        >
                      </template>
                      <template #no-options>
                        Sem cargo no recorte
                      </template>
                    </v-select>
                  </div>
                </div>
              </section>

              <section class="kpi-recorte__grupo">
                <h6 class="kpi-recorte__titulo">
                  <feather-icon
                    icon="GitMergeIcon"
                    size="13"
                  />
                  Estrutura
                </h6>
                <div class="kpi-recorte__campos">
                  <div class="kpi-recorte__campo">
                    <label for="matrixFranchises">Franquia digital</label>
                    <v-select
                      id="matrixFranchises"
                      v-model="criteria.franchiseIds"
                      :options="matrixOptions.franchises"
                      :reduce="option => option.id"
                      label="name"
                      multiple
                      :close-on-select="false"
                      placeholder="Todas as franquias"
                    >
                      <template #search="{ attributes, events }">
                        <input
                          class="vs__search"
                          v-bind="attributes"
                          :aria-label="attributes.placeholder || 'Franquia digital'"
                          v-on="events"
                        >
                      </template>
                      <template #no-options>
                        Sem franquia no recorte
                      </template>
                    </v-select>
                  </div>
                  <div class="kpi-recorte__campo">
                    <label for="matrixPhysical">Unidade física</label>
                    <v-select
                      id="matrixPhysical"
                      v-model="criteria.physicalFranchiseIds"
                      :options="matrixOptions.physicalFranchises"
                      :reduce="option => option.id"
                      label="name"
                      multiple
                      :close-on-select="false"
                      placeholder="Todas as unidades"
                    >
                      <template #search="{ attributes, events }">
                        <input
                          class="vs__search"
                          v-bind="attributes"
                          :aria-label="attributes.placeholder || 'Unidade física'"
                          v-on="events"
                        >
                      </template>
                      <template #no-options>
                        Sem unidade no recorte
                      </template>
                    </v-select>
                  </div>
                </div>
              </section>

              <section class="kpi-recorte__grupo">
                <h6 class="kpi-recorte__titulo">
                  <feather-icon
                    icon="PackageIcon"
                    size="13"
                  />
                  Produto
                </h6>
                <div class="kpi-recorte__campos">
                  <div class="kpi-recorte__campo">
                    <label for="matrixSolution">Solução financeira</label>
                    <v-select
                      id="matrixSolution"
                      ref="selSolucao"
                      v-model="financialSolutionIds"
                      :options="financialSolutions"
                      :reduce="option => option.id"
                      label="name"
                      multiple
                      :close-on-select="false"
                      placeholder="Todas as soluções"
                      @close="buscarSeMudou"
                      @option:deselected="buscarSeFechado('selSolucao')"
                    >
                      <template #search="{ attributes, events }">
                        <input
                          class="vs__search"
                          v-bind="attributes"
                          :aria-label="attributes.placeholder || 'Solução financeira'"
                          v-on="events"
                        >
                      </template>
                      <template #no-options>
                        Sem solução no catálogo
                      </template>
                    </v-select>
                  </div>
                  <div class="kpi-recorte__campo">
                    <label for="matrixCompany">
                      Empresa
                      <feather-icon
                        v-b-tooltip.hover.top="'A empresa parceira dona da solução.'"
                        icon="InfoIcon"
                        size="13"
                        class="kpi-recorte__info"
                      />
                    </label>
                    <v-select
                      id="matrixCompany"
                      ref="selEmpresa"
                      v-model="partnerCompanyIds"
                      :options="partnerCompanies"
                      :reduce="option => option.id"
                      label="name"
                      multiple
                      :close-on-select="false"
                      placeholder="Todas as empresas"
                      @close="buscarSeMudou"
                      @option:deselected="buscarSeFechado('selEmpresa')"
                    >
                      <template #search="{ attributes, events }">
                        <input
                          class="vs__search"
                          v-bind="attributes"
                          :aria-label="attributes.placeholder || 'Empresa'"
                          v-on="events"
                        >
                      </template>
                      <template #no-options>
                        Sem empresa no catálogo
                      </template>
                    </v-select>
                  </div>
                </div>
              </section>
            </div>

            <!-- AVANCADO: o que era a gaveta "Filtros" de admin. Dentro do
                 recorte, e nao numa gaveta a parte, porque sao filtros como os
                 outros -- e os mais consequentes: trocam a base inteira. -->
            <div
              v-if="showFiltersButton"
              class="kpi-recorte__avancado"
            >
              <h6 class="kpi-recorte__titulo kpi-recorte__titulo--inline">
                <feather-icon
                  icon="ShieldIcon"
                  size="13"
                />
                Avançado
                <b-badge
                  variant="light-primary"
                  class="ml-50"
                >admin</b-badge>
              </h6>
              <div class="kpi-recorte__opcao">
                <span>Valores</span>
                <b-form-radio-group
                  :checked="contractStatus"
                  size="sm"
                  buttons
                  button-variant="outline-secondary"
                  class="kpi-seg"
                  :options="contractStatusOptions"
                  @change="mudarAvancado('contractStatus', $event)"
                />
              </div>
              <div class="kpi-recorte__opcao">
                <span>Modo</span>
                <b-form-radio-group
                  :checked="snapshotMode"
                  size="sm"
                  buttons
                  button-variant="outline-secondary"
                  class="kpi-seg"
                  :options="snapshotModeOptions"
                  @change="mudarAvancado('snapshotMode', $event)"
                />
              </div>
              <b-form-checkbox
                v-for="opcao in opcoesAvancadas"
                :key="opcao.campo"
                :checked="$data[opcao.campo]"
                switch
                @change="mudarAvancado(opcao.campo, $event)"
              >
                {{ opcao.rotulo }}
              </b-form-checkbox>
            </div>

            <div class="kpi-recorte__rodape">
              <b-button
                size="sm"
                variant="flat-secondary"
                :disabled="!chipsDeRecorte.length"
                @click="limparRecorte"
              >
                Limpar recorte
              </b-button>
            </div>
          </div>
        </b-collapse>
      </b-card-body>
    </b-card>

    <!-- COMPONENTES COMO GAVETA LATERAL.
         Como acordeao, este painel empurrava a tabela uns 400px para baixo
         -- e a tabela e justamente o que se quer olhar enquanto se liga e
         desliga coluna. Na lateral, a arvore fica visivel atras e o efeito
         de cada switch se ve na hora. O recorte segue abrindo na vertical:
         ali o fluxo e preencher e mandar buscar, nao ver mudar. -->
    <b-sidebar
      id="sidebar-kpi-columns"
      v-model="showColumnsPanel"
      sidebar-class="sidebar-lg"
      bg-variant="white"
      shadow
      backdrop
      no-header
      right
    >
      <template #default="{ hide }">
        <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
          <h4 class="mb-0">
            Componentes e colunas
          </h4>
          <feather-icon
            class="ml-1 cursor-pointer"
            icon="XIcon"
            size="16"
            @click="hide"
          />
        </div>
        <div class="kpi-columns-panel kpi-columns-panel--sidebar">
          <!-- FRENTE 1 - COMPONENTES E COLUNAS.
                     Uma LINHA por macrocomponente, nao os 33 checkboxes de uma vez.
                     Com as cinco listas abertas ao mesmo tempo o painel virava uma
                     parede de quadradinhos laranja em que nao se achava nada -- e a
                     pergunta que a pessoa faz aqui e quase sempre de bloco ("tira
                     Momento X"), nao de coluna. A coluna a coluna continua
                     existindo, atras da seta. -->
          <div class="kpi-columns-panel__header">
            <p class="kpi-columns-panel__desc">
              Ligue ou desligue um macrocomponente inteiro, ou abra-o para
              escolher coluna por coluna. Sua escolha fica salva no seu usuário.
            </p>
            <div class="kpi-presets">
              <button
                type="button"
                class="kpi-preset"
                :class="{ 'kpi-preset--on': presetAtivo === 'padrao' }"
                @click="restoreDefaultColumns"
              >
                <feather-icon
                  icon="StarIcon"
                  size="13"
                />
                Padrão do produto
              </button>
              <button
                type="button"
                class="kpi-preset"
                :class="{ 'kpi-preset--on': presetAtivo === 'faturamento' }"
                @click="aplicarPreset('faturamento')"
              >
                <feather-icon
                  icon="DollarSignIcon"
                  size="13"
                />
                Só faturamento
              </button>
            </div>
          </div>

          <div class="kpi-columns-panel__body">
            <div
              v-for="group in groups"
              :key="group.key"
              class="kpi-group"
            >
              <div class="kpi-group__row">
                <button
                  type="button"
                  class="kpi-group__name"
                  :aria-expanded="gruposAbertos[group.key] ? 'true' : 'false'"
                  @click="toggleGrupoAberto(group.key)"
                >
                  <span
                    class="kpi-group__dot"
                    :style="{ '--group-base': group.base, '--group-ink-dark': group.inkDark }"
                  />
                  <span class="kpi-group__texts">
                    <span class="kpi-group__label">{{ group.label }}</span>
                    <span class="kpi-group__meta">{{ grupoMeta(group) }}</span>
                  </span>
                  <feather-icon
                    class="kpi-group__caret"
                    :icon="gruposAbertos[group.key] ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                    size="15"
                  />
                </button>
                <b-form-checkbox
                  switch
                  :checked="grupoLigado(group)"
                  :disabled="grupoObrigatorio(group)"
                  class="kpi-group__switch"
                  @change="toggleGrupoLigado(group)"
                />
              </div>

              <b-collapse :visible="!!gruposAbertos[group.key]">
                <div class="kpi-group__cols">
                  <button
                    v-for="column in group.columns"
                    :key="column.key"
                    type="button"
                    class="kpi-cbx"
                    :class="{
                      'kpi-cbx--on': selectedColumnKeys.includes(column.key),
                      'kpi-cbx--pinned': isRequiredColumn(column.key),
                    }"
                    :disabled="isRequiredColumn(column.key)"
                    :title="column.hint || column.label"
                    @click="toggleColumn(column.key)"
                  >
                    <span class="kpi-cbx__box">
                      <feather-icon
                        icon="CheckIcon"
                        size="9"
                      />
                    </span>
                    {{ column.label }}
                  </button>
                </div>
              </b-collapse>
            </div>
          </div>

          <div class="kpi-columns-panel__actions">
            <span class="kpi-columns-panel__count">
              <strong>{{ selectedColumnKeys.length }}</strong> de {{ totalDeColunas }} colunas visíveis
            </span>
            <!--
                    Não há botão "salvar minha visão", embora o canvas desenhe um: a
                    seleção já é persistida por usuário a cada mudança. Um botão de
                    salvar ao lado de algo que já salva sozinho faz o usuário achar
                    que o que ele não salvou se perdeu. Pela mesma razão não há chip
                    "Minha visão" ao lado dos outros dois presets -- minha visão é
                    sempre o estado atual, então o chip estaria sempre aceso.
                  -->
            <button
              type="button"
              class="kpi-columns-panel__restore"
              @click="restoreDefaultColumns"
            >
              restaurar padrão
            </button>
          </div>
        </div>
      </template>
    </b-sidebar>

    <!-- OS QUATRO TOTALIZADORES.
         Decompoem a mesma estrutura por RELACAO — o que e meu, o que e de quem
         responde a mim, o consolidado — e por isso nao acompanham o botao de
         recorte: os tres primeiros sao exatamente as tres leituras que o botao
         escolhe, e mostra-las juntas e o ponto. O botao decide o que a TABELA
         lista; estes cartoes ficam parados, para se poder comparar. -->
    <div
      v-if="mostraTiles"
      class="kpi-tiles"
    >
      <!-- So rotulo, (i) e valor. O subtitulo e a nota de rodape explicavam o
           numero em toda leitura; a explicacao foi para o (i), onde fica para
           quem precisa -- e os numeros que a nota carregava (fatia, niveis,
           LTV/CL) foram junto, porque todos ja estao na tabela. -->
      <button
        v-for="tile in tiles"
        :key="tile.key"
        type="button"
        class="kpi-tile"
        :class="{ 'kpi-tile--strong': tile.strong, 'kpi-tile--on': tile.scope && scope === tile.scope }"
        :aria-pressed="tile.scope ? String(scope === tile.scope) : null"
        :aria-label="`${tile.label}${tile.owner ? ` de ${tile.owner}` : ''}. ${tile.tip}`"
        @click="tile.scope && (scope = tile.scope)"
      >
        <span class="kpi-tile__head">
          <span
            class="kpi-tile__ico"
            :style="{
              '--tile-tint': tile.tint,
              '--tile-ink': tile.ink,
              '--tile-ink-dark': tile.inkDark,
            }"
          >
            <feather-icon
              :icon="tile.icon"
              size="16"
              class="kpi-tile__ico-glyph"
            />
          </span>
          <span class="kpi-tile__label">
            {{ tile.label }}
            <small
              v-if="tile.owner"
              class="kpi-tile__owner"
            >de {{ tile.owner }}</small>
          </span>
          <feather-icon
            v-b-tooltip.hover.top="tile.tip"
            icon="InfoIcon"
            size="14"
            class="kpi-tile__info"
          />
        </span>

        <strong class="kpi-tile__value">
          <kpi-animated-number
            :value="tile.value"
            :formatter="tile.formatter"
          />
        </strong>
      </button>
    </div>

    <b-card no-body>
      <!-- A explicacao de como a tela funciona vive no icone "i" ao lado do
           titulo, e nao fixa aqui: com a perspectiva virando filtro da mesma
           tabela, este bloco passaria a ocupar espaco em toda leitura. O que
           fica na tela e o que carrega NUMERO do recorte corrente — as linhas
           sem franquia e a fotografia, logo abaixo. -->

      <!-- O que a tela antiga descarta em silencio, esta diz. -->
      <div
        v-if="uncategorizedSummary.linhas && !loading"
        class="kpi-uncategorized-note"
      >
        <feather-icon
          icon="EyeOffIcon"
          size="13"
          class="mr-50"
        />
        <span>
          {{ uncategorizedSummary.linhas }} linhas sem franquia fora do total
          · {{ uncategorizedSummary.valor | toCurrency }}
        </span>
        <feather-icon
          v-b-tooltip.hover.top="'Mesma regra da tela antiga. Para incluí-las, ligue PNs out, Franqueados desativados e Redes desativadas em Recorte › Avançado.'"
          icon="InfoIcon"
          size="13"
          class="ml-50"
        />
      </div>

      <!-- A natureza do dado: parte do periodo e fotografia, parte esta viva.
           So aparece quando ha dia CONSOLIDADO. A tela roda em modo ao vivo por
           padrao, e ali a barra dizia sempre "N dias ao vivo, ainda sendo
           recalculados" — uma linha fixa que nao informa nada, porque nunca ha
           outra coisa a dizer. Ela volta sozinha quando alguem consultar uma
           competencia materializada, que e quando a distincao importa. -->
      <div
        v-if="snapshot.consolidated"
        class="kpi-photo"
      >
        <div
          class="kpi-photo__bar"
          role="img"
          :aria-label="snapshotLegenda"
        >
          <span
            class="kpi-photo__frozen"
            :style="{ width: `${Math.round(snapshot.ratio * 100)}%` }"
          />
        </div>
        <span class="kpi-photo__text">
          {{ snapshotLegenda }}
          <feather-icon
            v-b-tooltip.hover
            icon="InfoIcon"
            size="12"
            title="Dias consolidados foram materializados e não mudam mais. Os dias ao vivo são recalculados a cada leitura e ainda podem subir."
          />
        </span>
      </div>

      <!-- Primeira carga: a marca, porque não há forma nenhuma para espelhar
           ainda. Recargas: esqueleto com a forma da tabela, que é conhecida. -->
      <futuro-loader
        v-if="loading && !hasLoadedOnce"
        block
        size="88"
        show-label
        label="Montando o Faturômetro..."
      />

      <div
        v-else-if="erro"
        class="kpi-state"
      >
        <feather-icon
          icon="AlertTriangleIcon"
          size="28"
          class="kpi-state__icon"
        />
        <p class="kpi-state__text">
          {{ erro }}
        </p>
        <b-button
          variant="outline-primary"
          size="sm"
          @click="search"
        >
          Tentar de novo
        </b-button>
      </div>

      <kpi-tree-table
        v-else
        :perspective="perspective"
        :roots="scopedRoots"
        :columns="columns"
        :selected-key="detail.nodeKey"
        :scope.sync="scope"
        :show-outline.sync="showOutline"
        :only-with-movement.sync="criteria.onlyWithMovement"
        :min-total.sync="criteria.minTotal"
        :density.sync="density"
        :row-height="density === 'compacta' ? 32 : 40"
        :export-name="nomeDoExport"
        :loading="loading"
        :hidden-keys="hiddenNodeKeys"
        @cell-click="openDetail"
        @show-columns="showColumnsPanel = true"
      >
        <!-- Os avisos da arvore viraram um (i) com contador: eram duas frases
             inteiras disputando a barra com os controles. -->
        <template #toolbar>
          <span
            v-if="avisosDaArvore.length && !loading"
            v-b-tooltip.hover.bottom="avisosDaArvore.join(' ')"
            class="kpi-tree-note"
            tabindex="0"
            :aria-label="avisosDaArvore.join(' ')"
          >
            <feather-icon
              icon="InfoIcon"
              size="13"
            />
            {{ avisosDaArvore.length }}
          </span>
        </template>
        <template #empty>
          <span v-if="semLinhaPropria">
            Você não tem linha própria no período consultado, então
            "{{ scope === 'own' ? 'Própria' : 'Equipe direta' }}" fica vazia.
            Use "Estrutura" para ver a árvore inteira.
          </span>
          <span v-else-if="periodoAberto">
            A competência ainda está aberta e nenhum contrato entrou no recorte.
            Os números continuam mudando ao longo do dia.
          </span>
          <span v-else>
            Nenhum consultor no período e nos filtros escolhidos.
          </span>
        </template>
      </kpi-tree-table>
    </b-card>

    <kpi-detail-drawer
      :visible="detail.visible"
      :loading="detail.loading"
      :erro="detail.erro"
      :consultant-name="detail.consultantName"
      :items="detail.items"
      :meta="detail.meta"
      :ltv="detail.ltv"
      :network-amount="detail.networkAmount"
      :column-label="detail.columnLabel"
      :column-type="detail.columnType"
      :consultant-names="consultantNames"
      @close="closeDetail"
      @page="loadDetailPage"
    />
  </div>
</template>

<script>
import {
  BButton,
  BCard,
  BCardBody,
  BBadge,
  BCollapse,
  BSidebar,
  BFormCheckbox,
  BFormRadioGroup,
  BFormSelect,
  BSpinner,
  VBTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import { Portuguese } from 'flatpickr/dist/l10n/pt.js'
import ShortcutButtonsPlugin from 'shortcut-buttons-flatpickr'
import { mapActions, mapGetters } from 'vuex'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import { isFounderRole, isPnRole, ADMIN_CREDENTIALS_STORAGE_KEY } from '@/constants/auth'
import * as accountTypes from '@/modules/account/store/types'
import FuturoLoader from '@/modules/shared/components/FuturoLoader.vue'
import { getCredentials } from '@/helpers/credentials'
import * as types from '../store/types'
import KpiTreeTable from '../components/KpiTreeTable.vue'
import KpiDetailDrawer from '../components/KpiDetailDrawer.vue'
import KpiViewTabs from '../components/KpiViewTabs.vue'
import KpiAnimatedNumber from '../components/KpiAnimatedNumber.vue'

import {
  addTotals,
  buildTree,
  countContext,
  emptyTotals,
  findNodes,
  indexStructure,
  scopeRoots,
  sumContext,
  toNumber,
} from '../utils/kpiTree'
import {
  PRESETS,
  emYmd,
  isOpenPeriod,
  periodLabel,
  presetKeyOf,
  presetRange,
  resolvePeriod,
  shiftMonth,
  snapshotSummary,
} from '../utils/kpiPeriod'
import { toQuery, fromQuery, mesmaQuery } from '../utils/kpiUrlState'
import {
  availableFilters,
  dropUncategorized,
  emptyCriteria,
  filterRows,
  hiddenKeys,
} from '../utils/kpiFilters'
import {
  COLUMNS,
  DEFAULT_COLUMN_KEYS,
  columnByKey,
  columnsByGroup,
  isRequiredColumn,
  resolveColumns,
} from '../utils/kpiColumns'
import { conferirComACelula } from '../utils/kpiDetail'

/**
 * O Faturômetro em árvore.
 *
 * Tela NOVA, em rota própria, ao lado da antiga. Enquanto o comparador de
 * paridade não estiver zerado em staging as duas convivem — trocar a rota do
 * menu é o último passo, não o primeiro, e ter as duas lado a lado é o que
 * torna a conferência manual possível.
 *
 * DUAS CHAMADAS, UMA ÁRVORE
 *
 * /table traz número, /structure traz o esqueleto. Em paralelo: nenhuma depende
 * da outra, e a estrutura é cacheada por 30 min no backend, então na prática
 * quem manda no tempo é a tabela.
 *
 * O TOTAL NÃO PASSA PELA ÁRVORE
 *
 * A tira de cima soma as LINHAS, não os nós. São o mesmo número — cada
 * consultor é exatamente um nó —, e é justamente por isso que a soma direta é
 * a certa: ela continua correta mesmo se a árvore montar errado, e a diferença
 * entre as duas viraria um sintoma visível em vez de um total silenciosamente
 * torto.
 */
/**
 * A chave da preferencia de colunas — por AMBIENTE e por USUARIO.
 *
 * Era `kpi.tree.columns`, sem prefixo nenhum. localStorage e por origem, e a
 * origem e a mesma para todo mundo que usa aquele navegador: a preferencia
 * sobrevivia ao logout e o proximo usuario herdava a selecao do anterior.
 *
 * O prefixo de ambiente segue o mesmo padrao das credenciais
 * (`${VUE_APP_ENV_NAME}_token_admin`, em helpers/credentials.js), para local,
 * staging e producao no mesmo navegador nao se misturarem.
 *
 * Sem usuario identificado (tela aberta antes do boot terminar) a preferencia
 * fica anonima em vez de vazar para a proxima conta.
 */
const chaveDePreferencia = (assunto = 'columns') => {
  const ambiente = process.env.VUE_APP_ENV_NAME || 'app'

  let usuario = 'anonimo'
  try {
    const credenciais = getCredentials(ADMIN_CREDENTIALS_STORAGE_KEY)
    const user = (credenciais && credenciais.user) || {}
    // `id` PRIMEIRO, mas ele nao chega: o login devolve o usuario com id e o
    // /api/users/profile do boot sobrescreve por uma versao aparada que nao
    // tem o campo. Medido no navegador -- as chaves que sobram sao name, role,
    // email, cpf, cnpj, company_name, appify_consultant_id, user_role_id...
    // Sem o fallback, TODO mundo caia em 'anonimo' e a preferencia de coluna
    // passava a ser compartilhada por quem usasse o mesmo navegador, que e
    // exatamente o defeito que esta chave foi criada para consertar.
    //
    // `id` fica na frente para o dia em que o payload do profile voltar a
    // traze-lo; email e o unico identificador que existe hoje em todo usuario
    // (appify_consultant_id e nulo para quem nao e consultor).
    const identificador = user.id || user.email || user.appify_consultant_id
    if (identificador) {
      usuario = String(identificador)
    }
  } catch (e) {
    // Armazenamento bloqueado: a preferencia vale so a sessao.
  }

  // `assunto` separa as preferencias do mesmo usuario: as colunas e a
  // densidade das linhas vivem em chaves proprias, e o formato da de colunas
  // continua o mesmo de antes -- quem ja tinha escolha salva nao a perde.
  return `${ambiente}_kpi.tree.${assunto}.${usuario}`
}

/** A chave antiga, global do navegador. Existe so para ser apagada. */
const CHAVE_ANTIGA_SEM_NAMESPACE = 'kpi.tree.columns'

export default {
  name: 'KpiTreeView',
  components: {
    BButton,
    BCard,
    BCardBody,
    BBadge,
    BCollapse,
    BSidebar,
    BFormCheckbox,
    BFormRadioGroup,
    BFormSelect,
    BSpinner,
    FuturoLoader,
    KpiAnimatedNumber,
    KpiDetailDrawer,
    KpiTreeTable,
    KpiViewTabs,
    flatPickr,
    vSelect,
  },
  directives: { 'b-tooltip': VBTooltip },
  setup() {
    return { toast: useToast() }
  },
  data() {
    return {
      loading: false,
      hasLoadedOnce: false,
      erro: null,
      loadingConsultants: false,
      rangeDate: undefined,
      // O atalho que a pessoa CLICOU. Desempata quando dois atalhos dao a mesma
      // faixa -- no primeiro mes do trimestre, "Este trimestre" e "Este mes".
      atalhoEscolhido: null,
      consultant: [],
      rows: [],
      structure: [],
      roles: [],
      physicalFranchises: [],
      financialSolutions: [],
      partnerCompanies: [],
      // Filtro de produto e SERVIDOR, nao cliente: vai no payload, e nao no
      // `criteria`, que so esconde linha ja carregada. Se estivesse em
      // criteria, o totalizador continuaria contando o que a tela escondeu.
      financialSolutionIds: [],
      partnerCompanyIds: [],
      snapshotRanges: [],
      criteria: emptyCriteria(),
      contractStatus: 'valid',
      allConsultants: false,
      includeInactiveFranchises: false,
      includeInactiveNetworks: false,
      disableContractNetworks: false,
      includeEmployees: false,
      snapshotMode: 'live',
      // O que a ultima busca perguntou ao servidor. Campo que so aplica
      // sozinho (dropdown fechando, periodo escolhido) compara com isto antes
      // de buscar, para fechar um dropdown sem mexer nele nao custar 1,5 s.
      ultimaAssinatura: null,
      density: 'confortavel',
      showColumnsPanel: false,
      showMatrixPanel: false,
      perspective: 'owner',
      scope: 'network',
      showOutline: false,
      // Ids vindos da URL esperando a lista de consultores carregar: o filtro
      // de PN guarda objetos, e a URL so pode guardar id.
      pendingConsultantIds: [],
      selectedColumnKeys: [...DEFAULT_COLUMN_KEYS],
      // Todos os blocos comecam FECHADOS, como no canvas: o painel existe para
      // dizer o que esta ligado, e isso se le nas linhas de meta. Abrir tudo de
      // saida devolveria a parede de checkboxes que ele veio desfazer.
      gruposAbertos: {},
      // Os atalhos entram no proprio calendario, no lugar do "Hoje" -- que
      // alem de pouco util num seletor de FAIXA estava quebrado: o helper
      // compartilhado chama `this.search()` no escopo do modulo, onde `this` e
      // undefined, e o clique terminava num TypeError.
      //
      // `this` aqui e a instancia: data() roda com ela, e as setas herdam.
      flatPickrConfig: {
        altFormat: 'd/m/Y',
        altInput: true,
        mode: 'range',
        locale: Portuguese,
        plugins: [ShortcutButtonsPlugin({
          button: PRESETS.map(preset => ({
            label: preset.label,
            attributes: { class: 'kpi-calendario__atalho' },
          })),
          onClick: (indice, fp) => this.aplicarAtalhoDePeriodo(PRESETS[indice].key, fp),
        })],
        // A classe deixa o CSS do calendario valer so para este calendario:
        // o flatpickr pendura o popup no <body>, fora de qualquer escopo.
        onReady: [(datas, texto, fp) => fp.calendarContainer.classList.add('kpi-calendario')],
        onOpen: [(datas, texto, fp) => this.marcarAtalhoAtivo(fp)],
      },
      detail: {
        visible: false,
        loading: false,
        erro: null,
        nodeKey: null,
        consultantName: '',
        bucket: null,
        consultantId: null,
        digitalFranchiseId: null,
        ltv: null,
        networkAmount: null,
        columnLabel: null,
        columnType: null,
        cellAmount: null,
        items: [],
        meta: {},
      },
    }
  },
  computed: {
    ...mapGetters({
      consultants: types.CONSULTANTS,
      user: accountTypes.USER,
    }),
    groups() {
      return columnsByGroup()
    },
    periodoAberto() {
      return isOpenPeriod(resolvePeriod(this.rangeDate).endDate)
    },
    columns() {
      return resolveColumns(this.selectedColumnKeys)
    },
    /**
     * O MESMO índice que a árvore usa: chave exata, e o nó do consultor na
     * falta dela.
     *
     * Aqui era um Map por "consultor:franquia" puro, e por isso os recortes de
     * cargo, nível e unidade física não casavam nas 722 linhas cujo par
     * (consultor, franquia) não existe na estrutura — o filtro descartava a
     * linha por "não ter cargo" quando a pessoa tem cargo, só numa franquia
     * diferente da que ela faturou. Ver `indexStructure` em kpiTree.js.
     */
    structureByKey() {
      const indice = indexStructure(this.structure)
      return {
        get: chave => {
          const [consultor, franquia] = String(chave).split(':')
          return indice.get(consultor, franquia)
        },
      }
    },
    /**
     * A mesma regra da tela antiga: sem franquia, fora do total.
     *
     * `KpiList.vue:289` só mostra os "Não categorizados" com allConsultants E
     * includeInactiveFranchises E includeInactiveNetworks juntos. Replicar isso
     * é regra nº 1 — sem ele a tela nova mostrava R$ 3.636,30 a mais que a
     * antiga em junho/2026, e o comparador de paridade acusa.
     */
    showUncategorized() {
      return this.allConsultants && this.includeInactiveFranchises && this.includeInactiveNetworks
    },
    categorizedRows() {
      return dropUncategorized(this.rows, this.showUncategorized, this.perspective)
    },
    /**
     * As linhas depois do recorte por PESSOA (franquia, cargo, nível).
     *
     * É aqui que o totalizador muda quando o usuário recorta — e é a mesma
     * semântica do filtro de PN que a tela antiga já tinha. Os filtros de
     * EXIBIÇÃO ("só com movimento", valor mínimo) não passam por aqui de
     * propósito: eles escondem linha sem mexer em total.
     */
    filteredRows() {
      return filterRows(this.categorizedRows.rows, this.structureByKey, this.criteria)
    },
    /** Quanto ficou fora por não ter franquia — para a tela dizer, não esconder. */
    uncategorizedSummary() {
      const fora = this.categorizedRows.excluded
      const valor = fora.reduce((soma, row) => soma + (Number(row.total_contract_amount) || 0), 0)
      return { linhas: fora.length, valor }
    },
    tree() {
      return buildTree(this.filteredRows, this.structure)
    },
    /** O consultor de quem está logado — a âncora dos recortes Própria e Equipe direta. */
    meuConsultorId() {
      return this.user?.appify_consultant_id || null
    },
    /**
     * As raízes DEPOIS do recorte do botão.
     *
     * "Própria" e "Equipe direta" são ancorados em quem está logado: a primeira
     * traz só a linha dele, a segunda traz ele mais quem responde diretamente a
     * ele, sumindo com os níveis mais fundos. "Estrutura" traz tudo.
     */
    scopedRoots() {
      return scopeRoots(this.tree.roots, this.scope, this.meuConsultorId)
    },
    /**
     * Verdade quando o recorte ancorado não achou a pessoa na árvore.
     *
     * Acontece com quem não tem linha própria no período — a tela precisa dizer
     * isso, senão fica uma tabela em branco sem explicação.
     */
    semLinhaPropria() {
      return this.scope !== 'network'
        && !this.loading
        && this.tree.roots.length > 0
        && this.scopedRoots.length === 0
    },
    /**
     * De quem os tiles "Produção própria" e "Equipe direta" falam — e COMO essa
     * resposta foi escolhida.
     *
     * Primeiro quem está logado, com TODAS as linhas dele: quem fatura em mais
     * de uma franquia tem um nó por franquia, e a produção dele é a soma delas.
     * Ancorar num nó só mostrava R$ 0,00 para 26 consultores em junho/2026,
     * porque o nó alcançado primeiro era justamente a linha de legado zerada.
     *
     * Quando ele não está na árvore — um Founder que filtrou para o PN de outra
     * pessoa, um funcionário sem produção — cai na raiz, DESDE QUE haja só uma:
     * aí a árvore é a estrutura daquela pessoa e a decomposição continua
     * verdadeira, só muda de dono. Com várias raízes não existe resposta
     * honesta, e os dois primeiros tiles viram leitura posicional.
     *
     * A `origem` é devolvida junto de propósito. O rótulo precisa saber se a
     * âncora é o usuário ou uma raiz emprestada, e a pergunta anterior
     * ("o consultantId da âncora é diferente do meu?") era CEGA: com várias
     * linhas o id bate, então ela dizia "seu faturamento individual" sobre uma
     * fração dele. Proveniência não tem como errar; aparência tem.
     */
    ancoragem() {
      const meus = findNodes(this.tree.roots, this.meuConsultorId)
      if (meus.length) {
        return { nos: meus, origem: 'usuario' }
      }
      if (this.tree.roots.length === 1) {
        return { nos: this.tree.roots, origem: 'raiz' }
      }
      return { nos: [], origem: null }
    },
    tileAnchors() {
      return this.ancoragem.nos
    },
    /** `'usuario'`, `'raiz'` ou `null` — nunca inferido do que a âncora parece. */
    ancoraOrigem() {
      return this.ancoragem.origem
    },
    /** O caso raro fica VISÍVEL em vez de silencioso: o tile diz que somou N linhas. */
    ancorasMultiplas() {
      return this.tileAnchors.length > 1
    },
    mostraTiles() {
      return !this.loading && this.tree.roots.length > 0
    },
    /**
     * Os quatro totalizadores do topo.
     *
     * Os três primeiros somam sempre produção PRÓPRIA, nunca consolidado, pela
     * mesma razão da linha de Total da tabela: `totals` contaria a mesma pessoa
     * em cada nível acima dela. Assim "Estrutura completa" é de fato a soma dos
     * outros dois mais as subestruturas, e o número fecha.
     *
     * O quarto é razão, não soma, e por isso se calcula sobre o total da
     * estrutura em vez de somar os tickets de cada um.
     */
    totalDeColunas() {
      return COLUMNS.length
    },
    tiles() {
      const ancoras = this.tileAnchors
      const temAncora = ancoras.length > 0
      const estrutura = sumContext(this.tree.roots, 'network')
      const somaPropria = nos => nos.reduce((soma, no) => addTotals(soma, no.own), emptyTotals())

      // COM âncora, a decomposição é pessoal: eu, quem responde a mim, o resto.
      // SEM âncora — um funcionário sem produção própria olhando a empresa
      // inteira, com várias raízes —, ela vira posicional: o topo da árvore e
      // tudo abaixo dele. Os dois casos decompõem o MESMO total e fecham; o que
      // muda é o eixo, e os rótulos dizem qual está em uso.
      //
      // Os dois ramos viraram um: `topo` é sempre uma LISTA, e `abaixo` é sempre
      // o que responde a ela. Era essa distinção que escondia o defeito — a
      // âncora pessoal era `[um nó]` onde deveria ser as N linhas da pessoa.
      const topo = temAncora ? ancoras : this.tree.roots
      const abaixo = topo.reduce((lista, no) => lista.concat(no.children), [])
      const propria = somaPropria(topo)
      const direta = somaPropria(abaixo)

      const dinheiro = totais => toNumber(totais.total_contract_amount)
      const eps = totais => columnByKey('interview_contract_quantity').value(totais)
      // Razão sem denominador dá ZERO, não "—": é o que o `ratio` de
      // kpiColumns faz, é o que a tela antiga faz e é o que a coluna da tabela
      // vai mostrar na mesma leitura. O guarda abaixo existe só para um valor
      // genuinamente não-finito não virar "R$ NaN" na cara do usuário.
      const moeda = valor => (Number.isFinite(valor)
        ? this.$options.filters.toCurrency(valor)
        : '—')

      const totalEstrutura = dinheiro(estrutura)
      // "0% do total" ao lado de um valor que NAO e zero se le como erro. Quem
      // cabe abaixo de meio ponto vira "<1%", e o zero de verdade continua "0%".
      const fatia = (() => {
        if (!totalEstrutura || !dinheiro(propria)) {
          return '0%'
        }
        const pontos = Math.round((dinheiro(propria) / totalEstrutura) * 100)
        return pontos ? `${pontos}%` : '<1%'
      })()
      // A âncora emprestada de uma raiz única: o rótulo passa a dizer de quem é.
      // Sai da ORIGEM, e não de comparar ids — comparar id é cego a quem tem
      // várias linhas, porque o id bate e a produção não.
      //
      // Nome COMPLETO, e nao o primeiro, e visivel no cartao, e nao so no (i). Com
      // filtro de PN quem esta logado sai da arvore e a ancora vira a raiz que
      // sobrou; o rotulo continuava "Producao propria" e o (i) dizia "de Felipe" --
      // o primeiro nome de quem filtrou E do filtrado. Lia-se como a propria
      // producao de quem estava olhando.
      const deQuem = this.ancoraOrigem === 'raiz'
        ? String((topo[0] || {}).name || '').trim() || null
        : null

      const linhas = n => `${n} ${n === 1 ? 'linha' : 'linhas'}`
      const frase = texto => `${texto.charAt(0).toUpperCase()}${texto.slice(1)}.`

      const lista = [
        {
          key: 'own',
          scope: temAncora ? 'own' : null,
          label: temAncora ? 'Produção própria' : 'Topo da estrutura',
          owner: deQuem,
          hint: (() => {
            if (!temAncora) return 'produção própria de quem não responde a ninguém aqui'
            const base = deQuem ? `faturamento individual de ${deQuem}` : 'seu faturamento individual'
            return this.ancorasMultiplas
              ? `${base}, somando as ${ancoras.length} linhas de franquia`
              : base
          })(),
          icon: temAncora ? 'UserIcon' : 'AwardIcon',
          tint: '#f4fae3',
          ink: '#5c7c00',
          inkDark: '#b6e63a',
          value: dinheiro(propria),
          formatter: moeda,
          badge: temAncora ? `${eps(propria)} EP` : linhas(topo.length),
          note: `${fatia} do total da estrutura`,
        },
        {
          key: 'direct',
          scope: temAncora ? 'direct' : null,
          label: temAncora ? 'Equipe direta' : 'Segundo nível',
          owner: deQuem,
          hint: (() => {
            if (!temAncora) return 'produção própria de quem responde ao topo'
            return deQuem
              ? `produção própria de quem responde a ${deQuem}`
              : 'produção própria de quem responde a você'
          })(),
          icon: 'UsersIcon',
          tint: '#e3fafc',
          ink: '#007482',
          inkDark: '#3ddced',
          value: dinheiro(direta),
          formatter: moeda,
          badge: linhas(abaixo.length),
          note: 'sem as subestruturas abaixo delas',
        },
        {
          key: 'network',
          scope: 'network',
          label: 'Estrutura completa',
          owner: deQuem,
          hint: 'consolidado sem dupla contagem',
          icon: 'GitMergeIcon',
          tint: '#e7f9ef',
          ink: '#197b44',
          inkDark: '#4ddb92',
          strong: true,
          value: totalEstrutura,
          formatter: moeda,
          badge: `${countContext(this.tree.roots, 'network')} PNs`,
          note: `${this.matrixOptions.maxDepth + 1} ${this.matrixOptions.maxDepth ? 'níveis' : 'nível'} na árvore`,
        },
        {
          key: 'ticket',
          scope: null,
          label: 'Ticket médio por EP',
          hint: 'R$ de EP ÷ EPs pagas',
          icon: 'DollarSignIcon',
          tint: '#f3f2f7',
          ink: '#82868b',
          inkDark: '#b4b7bd',
          value: columnByKey('client_interview_contract_average').value(estrutura),
          formatter: moeda,
          badge: `${eps(estrutura)} EP`,
          note: `LTV/CL médio ${moeda(columnByKey('ltv_cl').value(estrutura))}`,
        },
      ]

      // O (i) de cada cartao junta o que era subtitulo e o que era rodape: a
      // explicacao e os numeros de contexto (fatia, linhas, niveis, LTV/CL).
      return lista.map(tile => ({
        ...tile,
        tip: `${frase(tile.hint)} ${tile.badge} · ${frase(tile.note)}`,
      }))
    },
    hiddenNodeKeys() {
      return hiddenKeys(this.scopedRoots, this.criteria)
    },
    /** Id -> nome, da lista que o filtro de PN já carrega. */
    consultantNames() {
      const porId = {}
      const lista = this.consultants || []
      lista.forEach(item => {
        porId[item.id] = item.name
      })
      this.rows.forEach(row => {
        const id = row.consultant_id || row.id
        if (id && !porId[id]) {
          porId[id] = row.name || row.consultant_name
        }
      })
      return porId
    },
    matrixOptions() {
      return availableFilters(
        this.categorizedRows.rows,
        this.structure,
        this.roles,
        this.physicalFranchises,
        { structureByKey: this.structureByKey, criteria: this.criteria },
      )
    },
    /**
     * Qual preset descreve a selecao atual, ou nenhum.
     *
     * Serve so para acender o chip. Compara conjunto, nao ordem: a selecao vive
     * num array por conveniencia do v-model, mas o significado dela e de
     * conjunto -- duas ordens diferentes das mesmas colunas sao a mesma visao.
     */
    presetAtivo() {
      const igual = (a, b) => a.length === b.length && [...a].sort().join() === [...b].sort().join()
      const atual = this.selectedColumnKeys

      if (igual(atual, DEFAULT_COLUMN_KEYS)) {
        return 'padrao'
      }
      const producao = this.groups.find(grupo => grupo.key === 'producao')
      if (producao && igual(atual, producao.columns.map(coluna => coluna.key))) {
        return 'faturamento'
      }
      return null
    },
    /**
     * Um chip por recorte ligado, para a tela dizer o que esta escondendo.
     *
     * O painel que guarda estes campos fica FECHADO por padrao, entao sem esta
     * fileira um filtro ativo e invisivel: o numero vem menor e nao ha nada na
     * tela explicando por que. Cada chip nomeia o campo, mostra o valor por
     * extenso -- id nenhum aparece aqui -- e desliga aquele recorte sozinho.
     *
     * `campo` e o que `limparChip` usa para saber o que zerar; e por isso que
     * cada entrada carrega o nome do campo em vez de um callback.
     */
    chipsDeRecorte() {
      const chips = []
      const nomes = (ids, catalogo) => (ids || [])
        .map(id => {
          const achado = (catalogo || []).find(item => String(item.id) === String(id))
          return achado ? achado.name : null
        })
        .filter(Boolean)
        .join(', ')

      const lista = (campo, rotulo, ids, catalogo) => {
        const valor = nomes(ids, catalogo)
        if (valor) {
          chips.push({
            key: campo, campo, rotulo, valor,
          })
        }
      }

      if (this.criteria.name) {
        chips.push({
          key: 'name', campo: 'name', rotulo: 'Nome', valor: this.criteria.name,
        })
      }
      lista('franchiseIds', 'Franquia', this.criteria.franchiseIds, this.matrixOptions.franchises)
      lista('physicalFranchiseIds', 'Unidade física', this.criteria.physicalFranchiseIds, this.matrixOptions.physicalFranchises)
      lista('roleIds', 'Cargo', this.criteria.roleIds, this.matrixOptions.roles)
      lista('financialSolutionIds', 'Solução', this.financialSolutionIds, this.financialSolutions)
      lista('partnerCompanyIds', 'Empresa', this.partnerCompanyIds, this.partnerCompanies)

      if (this.criteria.maxDepth !== null && this.criteria.maxDepth !== undefined) {
        chips.push({
          key: 'maxDepth',
          campo: 'maxDepth',
          rotulo: 'Até o nível',
          valor: String(this.criteria.maxDepth),
        })
      }
      // "So com movimento" e "Faturamento minimo" nao viram chip: sao de
      // Exibicao, e o botao Exibicao da tabela ja mostra quantos estao ligados.
      // Chip aqui e o que muda NUMERO.

      // A perspectiva nao e um filtro sobre as linhas, e troca a FONTE delas --
      // e a mudanca mais consequente que esta barra pode esconder. Entra aqui
      // justamente porque quem esquece ligada le a receita de outra pessoa.
      if (this.perspective !== 'owner') {
        chips.push({
          key: 'perspective', campo: 'perspective', rotulo: 'Atribuição', valor: 'Especialista',
        })
      }

      // Os de admin. Chegam tambem pela URL, entao aparecem para quem nao ve a
      // linha Avancado -- o chip e o unico jeito de essa pessoa saber que a
      // tela dela esta lendo brutos.
      if (this.contractStatus === 'gross') {
        chips.push({
          key: 'contractStatus', campo: 'contractStatus', rotulo: 'Valores', valor: 'Brutos',
        })
      }
      if (this.snapshotMode !== 'live') {
        chips.push({
          key: 'snapshotMode',
          campo: 'snapshotMode',
          rotulo: 'Modo',
          valor: this.snapshotMode === 'snapshot' ? 'Materializado' : 'Automático',
        })
      }
      this.opcoesAvancadas.forEach(opcao => {
        if (this[opcao.campo]) {
          chips.push({
            key: opcao.campo, campo: opcao.campo, rotulo: opcao.chipRotulo, valor: opcao.chipValor,
          })
        }
      })

      return chips
    },
    contractStatusOptions() {
      return [
        { value: 'valid', text: 'Validados' },
        { value: 'gross', text: 'Brutos' },
      ]
    },
    snapshotModeOptions() {
      return [
        { value: 'live', text: 'Ao vivo' },
        { value: 'snapshot', text: 'Materializado' },
      ]
    },
    /** Os interruptores de admin, com o texto do switch e o do chip. */
    opcoesAvancadas() {
      return [
        {
          campo: 'allConsultants', rotulo: 'PNs out', chipRotulo: 'Incluindo', chipValor: 'PNs out',
        },
        {
          campo: 'includeInactiveFranchises',
          rotulo: 'Franqueados desativados',
          chipRotulo: 'Incluindo',
          chipValor: 'franqueados desativados',
        },
        {
          campo: 'includeInactiveNetworks',
          rotulo: 'Redes desativadas',
          chipRotulo: 'Incluindo',
          chipValor: 'redes desativadas',
        },
        {
          campo: 'includeEmployees', rotulo: 'Colaboradores', chipRotulo: 'Incluindo', chipValor: 'colaboradores',
        },
        {
          campo: 'disableContractNetworks',
          rotulo: 'Sem verificação de redes de contrato',
          chipRotulo: 'Sem',
          chipValor: 'verificação de redes de contrato',
        },
      ]
    },
    /** A etiqueta ao lado do campo de período: "Este mês", "Agosto 2026"… */
    rotuloDoPeriodo() {
      const { startDate, endDate } = resolvePeriod(this.rangeDate)
      return periodLabel(startDate, endDate, undefined, this.atalhoEscolhido)
    },
    temProximoMes() {
      return !!shiftMonth(resolvePeriod(this.rangeDate).endDate, 1)
    },
    nomeDoExport() {
      const { startDate, endDate } = resolvePeriod(this.rangeDate)
      return `faturometro_${startDate}_${endDate}`
    },
    /** As notas da árvore, que viram um (i) com contador na barra da tabela. */
    avisosDaArvore() {
      const avisos = []
      if (this.tree.orphans) {
        avisos.push(`${this.tree.orphans} sem líder no recorte, realocados sob o ancestral mais próximo.`)
      }
      if (this.hiddenNodeKeys.size) {
        avisos.push(`${this.hiddenNodeKeys.size} escondidos pela Exibição — o total no topo da tabela segue completo.`)
      }
      return avisos
    },
    perspectiveOptions() {
      return [
        { value: 'owner', text: 'Dono da produção' },
        { value: 'specialist', text: 'Especialista' },
      ]
    },
    snapshot() {
      const { startDate, endDate } = resolvePeriod(this.rangeDate)
      return snapshotSummary(startDate, endDate, this.snapshotRanges)
    },
    snapshotLegenda() {
      const { consolidated, live, total } = this.snapshot
      if (!consolidated) {
        return `${total} ${total === 1 ? 'dia' : 'dias'} ao vivo, ainda sendo recalculados`
      }
      if (!live) {
        return `${total} ${total === 1 ? 'dia' : 'dias'} consolidados — esta competência não muda mais`
      }
      return `${consolidated} de ${total} dias consolidados, ${live} ainda ao vivo`
    },
    isPn() {
      return isPnRole(this.user?.user_contract_type_id)
    },
    isFounder() {
      return isFounderRole(this.user?.user_role_id)
    },
    showFiltersButton() {
      return !this.isPn || this.isFounder
    },
  },
  watch: {
    /** A densidade é preferência de quem olha, como as colunas: por usuário. */
    density(valor) {
      try {
        localStorage.setItem(chaveDePreferencia('density'), valor)
      } catch (e) {
        // Armazenamento bloqueado: a preferência vale só a sessão.
      }
    },
  },
  mounted() {
    // Antes de qualquer busca: a URL manda no recorte inicial.
    this.hydrateFromUrl()
    this.restoreColumnPreference()
    this.restoreDensityPreference()
    this.loadConsultants()
    this.search()
  },
  methods: {
    ...mapActions({
      getKpiTable: types.GET_CONSULTANT_AND_CHILDS,
      getStructure: types.GET_CONSULTANT_STRUCTURE,
      getKpiSnapshotRanges: types.GET_KPI_SNAPSHOT_RANGES,
      getContractDetail: types.GET_CONTRACT_DETAIL,
      getConsultants: types.GET_CONSULTANTS,
    }),
    /**
     * O recorte da URL entra no estado — e o que torna o link compartilhavel,
     * o botao voltar util e o F5 inofensivo.
     *
     * Roda uma vez, antes da primeira busca. Nada aqui confia na query: quem
     * valida e descarta lixo e o kpiUrlState, para `?recorte=banana` nunca
     * chegar ao backend.
     */
    hydrateFromUrl() {
      const daUrl = fromQuery(this.$route.query || {})

      if (daUrl.startDate && daUrl.endDate) {
        this.rangeDate = `${daUrl.startDate} até ${daUrl.endDate}`
      } else {
        // Sem período na URL, a tela precisa semear um. Não semear não deixa o
        // campo vazio: `resolvePeriod(undefined)` cai em HOJE..HOJE
        // (kpiPeriod.js:42-45), e a primeira carga pergunta ao backend UM DIA
        // só. O resultado é um Faturômetro inteiro zerado logo ao abrir --
        // enquanto a árvore aparece normalmente, porque /structure é chamado
        // com `asOf` e não depende de faturamento. Parece tela quebrada, e o
        // usuário não tem como adivinhar que o problema é o recorte.
        //
        // A semente é a competência corrente, do dia 1 até hoje: é a pergunta
        // que alguém abrindo a tela quer fazer.
        //
        // emYmd e NÃO toISOString().split('T')[0] (o que KpiList.vue:382 faz):
        // toISOString converte para UTC e, a leste de Greenwich depois das 21h,
        // devolve o dia seguinte. Esse off-by-one já queimou esta base uma vez,
        // no calendário do Oráculo.
        const hoje = new Date()
        const primeiroDoMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1)
        this.rangeDate = `${emYmd(primeiroDoMes)} até ${emYmd(hoje)}`
      }

      const simples = ['scope', 'perspective', 'contractStatus', 'snapshotMode', 'showOutline',
        'financialSolutionIds', 'partnerCompanyIds',
        'allConsultants', 'includeInactiveFranchises', 'includeInactiveNetworks',
        'disableContractNetworks', 'includeEmployees']
      simples.forEach(campo => {
        if (daUrl[campo] !== undefined) {
          this[campo] = daUrl[campo]
        }
      })

      if (daUrl.criteria) {
        this.criteria = { ...this.criteria, ...daUrl.criteria }
      }

      // O filtro de PN so pode ser remontado quando a lista existir.
      this.pendingConsultantIds = daUrl.consultantIds || []
    },
    /**
     * Estado -> URL, a cada busca.
     *
     * `replace`, e nao `push`: uma busca nao e uma pagina nova. Com `push`, dez
     * ajustes de filtro virariam dez voltas no botao voltar antes de sair da
     * tela.
     *
     * O `catch` vazio existe porque o vue-router rejeita navegacao redundante
     * como erro; `mesmaQuery` ja evita o caso comum, e o catch cobre a corrida
     * entre duas buscas rapidas.
     */
    syncUrl() {
      const { startDate, endDate } = resolvePeriod(this.rangeDate)
      const query = toQuery({
        startDate,
        endDate,
        scope: this.scope,
        perspective: this.perspective,
        contractStatus: this.contractStatus,
        snapshotMode: this.snapshotMode,
        showOutline: this.showOutline,
        allConsultants: this.allConsultants,
        includeInactiveFranchises: this.includeInactiveFranchises,
        includeInactiveNetworks: this.includeInactiveNetworks,
        disableContractNetworks: this.disableContractNetworks,
        includeEmployees: this.includeEmployees,
        criteria: this.criteria,
        financialSolutionIds: this.financialSolutionIds,
        partnerCompanyIds: this.partnerCompanyIds,
        consultantIds: this.consultant.map(item => item.id),
      })

      if (mesmaQuery(query, this.$route.query || {})) {
        return
      }

      this.$router.replace({ query }).catch(() => {})
    },
    buildPayload() {
      // Período vazio vira HOJE..HOJE, igual ao backend. Ver kpiPeriod.js.
      const { startDate, endDate } = resolvePeriod(this.rangeDate)

      return {
        startDate,
        endDate,
        kpiContractStatus: this.contractStatus,
        snapshotMode: this.snapshotMode,
        allConsultants: this.allConsultants,
        includeInactiveFranchises: this.includeInactiveFranchises,
        includeInactiveNetworks: this.includeInactiveNetworks,
        disableContractNetworks: this.disableContractNetworks,
        includeEmployees: this.includeEmployees,
        financialSolutionIds: this.financialSolutionIds,
        partnerCompanyIds: this.partnerCompanyIds,
      }
    },
    /**
     * O que uma busca pergunta ao servidor, como texto comparável.
     *
     * PN entra porque o filtro de PN descarta linhas da resposta
     * (applyConsultantFilter): tirar um PN precisa buscar de novo para elas
     * voltarem. A perspectiva entra porque troca a rota.
     */
    assinaturaDaBusca() {
      return JSON.stringify({
        ...this.buildPayload(),
        perspective: this.perspective,
        pn: this.consultant.map(item => item.id),
      })
    },
    /** A busca dos campos que aplicam sozinhos: só vai ao servidor se algo mudou. */
    buscarSeMudou() {
      if (this.assinaturaDaBusca() !== this.ultimaAssinatura) {
        this.search()
      }
    },
    /**
     * Tirar uma opção pelo x da etiqueta, com o dropdown FECHADO, também busca.
     * Com ele aberto quem busca é o `close`, uma vez só, no fim da escolha.
     */
    buscarSeFechado(ref) {
      this.$nextTick(() => {
        const campo = this.$refs[ref]
        if (!campo || !campo.open) {
          this.buscarSeMudou()
        }
      })
    },
    search() {
      this.syncUrl()
      this.loading = true
      this.erro = null
      this.closeDetail()
      const payload = this.buildPayload()
      this.ultimaAssinatura = this.assinaturaDaBusca()
      // Com os campos aplicando sozinhos, duas buscas podem se cruzar (um
      // dropdown fechando enquanto a anterior ainda carrega). Só a mais nova
      // escreve na tela; a resposta atrasada de uma pergunta velha é descartada.
      this.buscaAtual = (this.buscaAtual || 0) + 1
      const estaBusca = this.buscaAtual
      const atual = () => estaBusca === this.buscaAtual

      return Promise.all([
        this.fetchRows(payload),
        // A estrutura DA COMPETENCIA consultada, nao a de hoje. Olhar agosto
        // com a arvore de hoje mostraria quem trocou de lider depois no lugar
        // novo, e esconderia quem saiu mas produziu naquele mes — sem mexer em
        // nenhum total, que e o que torna o defeito invisivel.
        this.getStructure({
          asOf: payload.endDate,
          includeInactiveFranchises: payload.includeInactiveFranchises,
          includeInactiveNetworks: payload.includeInactiveNetworks,
        }),
        // Quais dias já são fotografia. Falhar aqui não pode derrubar a tela: o
        // indicador some e os números continuam os mesmos.
        this.getKpiSnapshotRanges(payload).catch(() => ({ data: { ranges: [] } })),
      ])
        .then(([tabela, estrutura, faixas]) => {
          if (!atual()) {
            return
          }
          this.rows = this.applyConsultantFilter(this.extractRows(tabela))
          this.structure = (estrutura.data && estrutura.data.nodes) || []
          this.roles = (estrutura.data && estrutura.data.roles) || []
          this.physicalFranchises = (estrutura.data && estrutura.data.physical_franchises) || []
          this.financialSolutions = (estrutura.data && estrutura.data.financial_solutions) || []
          this.partnerCompanies = (estrutura.data && estrutura.data.partner_companies) || []
          this.snapshotRanges = (faixas.data && faixas.data.ranges) || []
        })
        .catch(() => {
          if (!atual()) {
            return
          }
          this.rows = []
          this.structure = []
          this.snapshotRanges = []
          this.erro = 'Não foi possível carregar o Faturômetro. '
            + 'Se continuar, avise o setor de TI.'
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Oops!',
              text: 'Ocorreu um erro ao recuperar os dados de KPI. Entre em contato com o setor de TI.',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          if (!atual()) {
            return
          }
          this.loading = false
          this.hasLoadedOnce = true
        })
    },
    /**
     * A MESMA rota nas duas leituras.
     *
     * Antes, a perspectiva de especialista chamava um endpoint paralelo que so
     * cobria contratos — e por isso 27 das 33 colunas apareciam como `0`. Agora
     * e `/table` com o modo de atribuicao: mesma agregacao, mesmas colunas, so
     * muda quem recebe o credito de cada fato.
     */
    fetchRows(payload) {
      if (this.perspective === 'specialist') {
        return this.getKpiTable({ ...payload, attribution: 'specialist', skipCommit: true })
      }
      return this.getKpiTable({ ...payload, skipCommit: true })
    },
    extractRows(resposta) {
      // As duas leituras vem por /table e tem a mesma forma: uma lista de linhas.
      return resposta.data || []
    },
    /**
     * O filtro de PN recorta as LINHAS, não a árvore. Quem sobrar sem líder no
     * recorte é realocado por buildTree sob o ancestral mais próximo que ficou.
     */
    applyConsultantFilter(rows) {
      if (!this.consultant.length) {
        return rows
      }
      const escolhidos = new Set(this.consultant.map(item => item.id))
      return rows.filter(row => escolhidos.has(row.id || row.consultant_id))
    },
    loadConsultants() {
      this.loadingConsultants = true
      this.getConsultants()
        .catch(() => {})
        .finally(() => {
          this.loadingConsultants = false
          this.restoreConsultantsFromUrl()
        })
    },
    /**
     * Remonta o filtro de PN a partir dos ids da URL.
     *
     * O v-select guarda o OBJETO do consultor e a URL so pode guardar id, e a
     * lista chega depois — entao a hidratacao espera aqui. Ids que nao existem
     * mais na lista somem em silencio: um consultor removido nao pode travar a
     * tela de quem abriu um link antigo.
     *
     * Nao dispara busca: quem chamou ja vai buscar, e dois fetches na abertura
     * era o que este `if` evita.
     */
    restoreConsultantsFromUrl() {
      if (!this.pendingConsultantIds.length) {
        return
      }

      const querIds = new Set(this.pendingConsultantIds)
      this.consultant = (this.consultants || []).filter(item => querIds.has(item.id))
      this.pendingConsultantIds = []

      if (this.consultant.length) {
        this.rows = this.applyConsultantFilter(this.rows)
      }
      // O PN que volta da URL recorta a MESMA resposta (o filtro e local): a
      // busca que ja foi, ou esta indo, serve. Sem isto a assinatura ficava
      // desatualizada e o primeiro dropdown fechado buscaria tudo de novo.
      this.ultimaAssinatura = this.assinaturaDaBusca()
    },
    /**
     * Desliga UM recorte pelo x do chip, sem abrir o painel.
     *
     * Só vai ao servidor o que o servidor soma. Cargo, franquia, unidade, nome
     * e nível são recortes LOCAIS sobre linhas já carregadas: a árvore e os
     * totalizadores se recalculam sozinhos, e buscar de novo custava ~1,5 s para
     * receber a mesma resposta.
     */
    limparChip(chip) {
      const LOCAIS = ['name', 'franchiseIds', 'physicalFranchiseIds', 'roleIds', 'maxDepth']
      const PADRAO_ADMIN = {
        contractStatus: 'valid',
        snapshotMode: 'live',
        allConsultants: false,
        includeInactiveFranchises: false,
        includeInactiveNetworks: false,
        includeEmployees: false,
        disableContractNetworks: false,
      }

      if (LOCAIS.includes(chip.campo)) {
        this.$set(this.criteria, chip.campo, emptyCriteria()[chip.campo])
        return
      }

      if (chip.campo === 'perspective') {
        this.changePerspective('owner')
        return
      }

      if (chip.campo === 'financialSolutionIds' || chip.campo === 'partnerCompanyIds') {
        this[chip.campo] = []
      } else if (chip.campo in PADRAO_ADMIN) {
        this[chip.campo] = PADRAO_ADMIN[chip.campo]
      }

      this.search()
    },
    /**
     * Limpa o recorte inteiro — e SÓ o recorte.
     *
     * "Só com movimento" e "Faturamento mínimo" são de Exibição e ficam como
     * estão: quem limpa os filtros da soma não pediu para a tabela voltar a
     * mostrar as linhas zeradas. Solução, empresa e os de admin vivem no topo
     * do estado, fora de `criteria`, e por isso são zerados um a um.
     *
     * Só busca se algo que o servidor soma mudou; cargo, franquia e unidade se
     * resolvem na tela.
     */
    limparRecorte() {
      const { onlyWithMovement, minTotal } = this.criteria
      this.criteria = { ...emptyCriteria(), onlyWithMovement, minTotal }
      this.perspective = 'owner'
      this.financialSolutionIds = []
      this.partnerCompanyIds = []
      this.contractStatus = 'valid'
      this.snapshotMode = 'live'
      this.opcoesAvancadas.forEach(opcao => {
        this[opcao.campo] = false
      })
      this.buscarSeMudou()
    },
    /** Um controle da linha Avançado: aplica e busca, porque todos mudam a base. */
    mudarAvancado(campo, valor) {
      this[campo] = valor
      this.buscarSeMudou()
    },

    // ---- Período -----------------------------------------------------------
    /**
     * O atalho escreve a faixa NO CALENDÁRIO, e não só em `rangeDate`.
     *
     * Ao fechar, o vue-flatpickr devolve ao v-model a faixa que está no
     * calendário. Mudar só `rangeDate` e fechar em seguida fazia o fechamento
     * escrever a faixa antiga por cima: o atalho acendia, a busca da faixa nova
     * saía e era atropelada pela da antiga, e o período não mudava. A busca fica
     * com `aoFecharCalendario`, que recebe a faixa já nova.
     */
    aplicarAtalhoDePeriodo(chave, fp) {
      const { startDate, endDate } = presetRange(chave)
      this.atalhoEscolhido = chave
      fp.setDate([startDate, endDate], true)
      fp.close()
    },
    /** ‹ e ›: a competência inteira antes ou depois da que se está vendo. */
    andarMes(passo) {
      const faixa = shiftMonth(resolvePeriod(this.rangeDate).endDate, passo)
      if (!faixa) {
        return
      }
      this.atalhoEscolhido = null
      this.rangeDate = `${faixa.startDate} até ${faixa.endDate}`
      this.buscarSeMudou()
    },
    /**
     * Fechar o calendário com a faixa completa busca.
     *
     * Com uma data só (o primeiro clique, e a pessoa desistiu) não busca: o
     * recorte de um dia raramente é o que se quis, e era o que a tela
     * perguntaria. Os atalhos também buscam por aqui: eles escrevem a faixa no
     * calendário e fecham.
     */
    aoFecharCalendario(datas) {
      if (datas && datas.length === 2) {
        this.$nextTick(() => this.buscarSeMudou())
      }
    },
    /** Acende, no calendário, o atalho que descreve o período atual. */
    marcarAtalhoAtivo(fp) {
      const { startDate, endDate } = resolvePeriod(this.rangeDate)
      const ativo = presetKeyOf(startDate, endDate, undefined, this.atalhoEscolhido)
      const botoes = fp.calendarContainer.querySelectorAll('.kpi-calendario__atalho')
      botoes.forEach((botao, indice) => {
        botao.classList.toggle('is-active', PRESETS[indice] && PRESETS[indice].key === ativo)
      })
    },
    /**
     * Trocar a perspectiva troca a FONTE das linhas, não um filtro sobre elas.
     *
     * §25: "o filtro de especialista deverá modificar a perspectiva de
     * atribuição, e não apenas localizar contratos em que existe um
     * especialista". São duas leituras da mesma receita, e nunca se somam.
     */
    changePerspective(valor) {
      if (this.perspective === valor) {
        return
      }
      this.perspective = valor
      this.search()
    },

    // ---- Componentes (colunas) -------------------------------------------
    isRequiredColumn,
    toggleColumn(key) {
      if (isRequiredColumn(key)) {
        return
      }
      const selecionadas = new Set(this.selectedColumnKeys)
      if (selecionadas.has(key)) {
        selecionadas.delete(key)
      } else {
        selecionadas.add(key)
      }
      this.selectedColumnKeys = [...selecionadas]
      this.persistColumnPreference()
    },
    toggleGrupoAberto(chave) {
      this.$set(this.gruposAbertos, chave, !this.gruposAbertos[chave])
    },
    /**
     * Um bloco esta LIGADO quando tem ao menos uma coluna visivel.
     *
     * Nao e "todas visiveis", e a diferenca importa: com a selecao padrao tres
     * dos cinco blocos ficam parciais, entao a regra do "todas" desenharia um
     * switch DESLIGADO ao lado de uma linha dizendo "6 de 9 colunas visiveis".
     * Ligado/desligado e escolha coluna a coluna sao dois estados
     * independentes -- e assim que o canvas trata, e e a unica leitura que nao
     * mente.
     */
    grupoLigado(group) {
      return group.columns.some(column => this.selectedColumnKeys.includes(column.key))
    },
    /** O bloco que carrega uma coluna obrigatoria nao pode ser desligado. */
    grupoObrigatorio(group) {
      return group.columns.some(column => isRequiredColumn(column.key))
    },
    /**
     * Desligar esvazia o bloco; religar traz o bloco INTEIRO.
     *
     * Religar nao devolve a selecao anterior de proposito. O switch e o
     * controle grosso -- "quero este assunto na tabela" -- e quem o liga espera
     * o assunto inteiro; devolver metade das colunas porque ha uma sessao atras
     * alguem tinha desmarcado duas seria uma memoria que a tela nao mostra em
     * lugar nenhum e que o usuario nao pediu. O ajuste fino continua a um
     * clique, atras da seta.
     */
    toggleGrupoLigado(group) {
      const selecionadas = new Set(this.selectedColumnKeys)
      const chaves = group.columns.map(column => column.key)

      if (this.grupoLigado(group)) {
        chaves.filter(key => !isRequiredColumn(key)).forEach(key => selecionadas.delete(key))
      } else {
        chaves.forEach(key => selecionadas.add(key))
      }

      this.selectedColumnKeys = [...selecionadas]
      this.persistColumnPreference()
    },
    /** A linha de contexto do bloco: quanto esta ligado, ou que esta oculto. */
    grupoMeta(group) {
      const total = group.columns.length
      const visiveis = group.columns
        .filter(column => this.selectedColumnKeys.includes(column.key)).length

      if (!visiveis) {
        return `oculto · ${total} ${total === 1 ? 'coluna' : 'colunas'}`
      }
      return `${visiveis} de ${total} ${total === 1 ? 'coluna visível' : 'colunas visíveis'}`
    },
    /**
     * Atalhos de leitura. Hoje só 'faturamento': quem abre a tela para
     * conferir dinheiro não quer atravessar as cinco faixas do funil para
     * chegar nas colunas de R$.
     *
     * As obrigatórias voltam sozinhas por resolveColumns, então um preset não
     * consegue deixar a tabela sem a coluna que identifica a linha.
     */
    aplicarPreset(nome) {
      if (nome !== 'faturamento') {
        return
      }

      const doGrupo = this.groups.find(grupo => grupo.key === 'producao')
      this.selectedColumnKeys = (doGrupo ? doGrupo.columns : []).map(coluna => coluna.key)
      this.persistColumnPreference()
    },
    restoreDefaultColumns() {
      this.selectedColumnKeys = [...DEFAULT_COLUMN_KEYS]
      this.persistColumnPreference()
    },
    persistColumnPreference() {
      try {
        localStorage.setItem(chaveDePreferencia(), JSON.stringify(this.selectedColumnKeys))
      } catch (e) {
        // Navegador com armazenamento bloqueado: a preferência vale só a sessão.
        // Nada aqui justifica interromper a tela.
      }
    },
    restoreDensityPreference() {
      try {
        const salva = localStorage.getItem(chaveDePreferencia('density'))
        if (salva === 'compacta' || salva === 'confortavel') {
          this.density = salva
        }
      } catch (e) {
        // Sem armazenamento, fica a confortável.
      }
    },
    restoreColumnPreference() {
      try {
        // A chave antiga era global do navegador. Ela e APAGADA e nao migrada:
        // migrar carregaria para esta conta a preferencia de quem usou o
        // navegador antes, que e justamente o defeito.
        localStorage.removeItem(CHAVE_ANTIGA_SEM_NAMESPACE)

        const salvo = JSON.parse(localStorage.getItem(chaveDePreferencia()) || 'null')
        // Filtra contra o catálogo: coluna renomeada ou removida numa versão
        // futura não pode deixar o usuário com a tela vazia e sem saber por quê.
        // Coluna renomeada ou removida numa versão futura não pode deixar o
        // usuário com a tela vazia sem saber por quê; e a obrigatória volta
        // mesmo que a preferência salva seja de antes de ela existir.
        const validas = Array.isArray(salvo) ? salvo.filter(columnByKey) : []
        if (validas.length) {
          this.selectedColumnKeys = validas
        }
      } catch (e) {
        this.selectedColumnKeys = [...DEFAULT_COLUMN_KEYS]
      }
    },

    // ---- Detalhamento -----------------------------------------------------
    openDetail({ node, column, bucket }) {
      const payload = this.buildPayload()

      this.detail = {
        ...this.detail,
        visible: true,
        loading: true,
        erro: null,
        nodeKey: node.key,
        consultantName: node.name,
        bucket,
        consultantId: node.consultantId,
        digitalFranchiseId: node.digitalFranchiseId,
        // O LTV mostrado é o da PRÓPRIA produção do consultor, não o do
        // subtotal da rede: a gaveta detalha a célula dele, não a da equipe.
        //
        // A guarda é a MESMA da coluna: sem cliente, não há LTV por cliente, e a
        // figura some (o componente esconde null). Aqui a guarda perguntava por
        // EP, sobra de quando o LTV dividia por EP -- e depois do card
        // 1217381846456640 um consultor com clientes de assinatura e nenhuma EP
        // tinha LTV na tabela e nenhum na gaveta.
        ltv: (() => {
          const valor = columnByKey('ltv_cl').value(node.own)
          return Number.isFinite(valor) ? valor : null
        })(),
        // Na leitura "Rede" a célula mostra o subtotal da equipe, mas o
        // detalhamento só existe por consultor — o backend lista os contratos
        // de UM consultor, nunca de uma subárvore. Sem dizer isso, "Valor da
        // célula: R$ 30 mil" apareceria embaixo de uma célula escrita R$ 500
        // mil, e a gaveta inteira perderia a credibilidade que existe para dar.
        networkAmount: this.scope !== 'own' && node.children.length
          ? column.value(this.scope === 'direct' ? node.directTotals : node.totals)
          : null,
        // A gaveta precisa saber O QUE foi clicado para formatar e conferir
        // certo. Nove colunas abrem gaveta e QUATRO delas sao CONTAGEM (EP,
        // Suporte, Acompanhamentos, Renovacoes): formatar o numero delas como
        // moeda fazia a celula "1" abrir dizendo "R$ 1,00".
        columnLabel: column.label,
        columnType: column.type,
        // O VALOR DA CELULA CLICADA, guardado no clique.
        //
        // E a referencia do "confere" nas duas perspectivas: dinheiro contra a
        // soma dos itens, contagem contra o numero de itens (ver
        // conferirComACelula). O backend manda um `cell_amount` proprio, mas
        // ele e recalculado e nao e a celula -- com filtro de Solucao ele vinha
        // sem filtro e "conferia" assim mesmo.
        cellAmount: column.value(node.own),
        items: [],
        meta: {},
      }

      this.fetchDetail(payload, 1)
    },
    loadDetailPage(page) {
      this.fetchDetail(this.buildPayload(), page)
    },
    fetchDetail(payload, page) {
      this.detail = { ...this.detail, loading: true, erro: null }

      // UMA gaveta para as duas perspectivas.
      //
      // A de especialista vinha de /specialist_production, que listava TODOS os
      // contratos da pessoa — sem balde, sem pagamento, sem filtro: clicar em
      // "EP 1" listava EP, acompanhamento e seguro, e a tela comparava a
      // contagem 1 com R$ 11 mil. Agora é o /contract_detail com
      // `attribution=specialist`: o mesmo balde e a mesma regra da tabela, com o
      // crédito em quem executou e o DONO em cada item (§32).
      return this.getContractDetail({
        consultantId: this.detail.consultantId,
        digitalFranchiseId: this.detail.digitalFranchiseId,
        startDate: payload.startDate,
        endDate: payload.endDate,
        bucket: this.detail.bucket,
        kpiContractStatus: payload.kpiContractStatus,
        // O FILTRO DE PRODUTO VAI JUNTO. Sem ele a gaveta listava tudo do
        // consultor embaixo de uma célula filtrada por Solução ou Empresa.
        financialSolutionIds: payload.financialSolutionIds,
        partnerCompanyIds: payload.partnerCompanyIds,
        attribution: this.perspective === 'specialist' ? 'specialist' : undefined,
        page,
        perPage: 25,
      })
        .then(({ data }) => {
          this.detail = {
            ...this.detail,
            items: data.items || [],
            // "Confere" contra o valor CLICADO, e não contra o agregado que o
            // backend recalcula: uma diferença entre a leitura da tabela e a da
            // gaveta tem de acender a tarja, não passar como conferida.
            meta: conferirComACelula(data.meta || {}, this.detail.cellAmount, this.detail.columnType),
          }
        })
        .catch(() => {
          this.detail = {
            ...this.detail,
            items: [],
            erro: 'Não foi possível carregar o detalhamento desta célula.',
          }
        })
        .finally(() => {
          this.detail = { ...this.detail, loading: false }
        })
    },
    closeDetail() {
      this.detail = {
        ...this.detail, visible: false, nodeKey: null, items: [], meta: {},
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.kpi-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 3rem 1.5rem;
  text-align: center;
}

.kpi-state__icon {
  color: #ea5455;
}

.kpi-state__text {
  margin: 0;
  color: var(--kpi-ink-muted, #b9b9c3);
}

/* O aviso das linhas sem franquia: uma pilula com o numero; como inclui-las
   fica no (i). Era um paragrafo de tres linhas em laranja em toda leitura. */
.kpi-uncategorized-note {
  display: inline-flex;
  align-items: center;
  margin: 0 1.5rem 0.85rem;
  padding: 0.3rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.786rem;
  font-weight: 500;
  background: rgba(255, 159, 67, 0.12);
  color: var(--kpi-warning-fg, #d97a1e);
}

/* Os avisos da arvore, na barra da tabela: um (i) com contador. */
.kpi-tree-note {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  height: 26px;
  padding: 0 0.6rem;
  border-radius: 13px;
  font-size: 0.786rem;
  font-weight: 600;
  background: var(--kpi-chip-bg, #f3f2f7);
  color: var(--kpi-chip-fg, #5e5873);
  cursor: help;
  outline: none;
}

.kpi-tree-note:focus-visible {
  box-shadow: 0 0 0 2px rgba(242, 98, 55, 0.45);
}

/* ---------------------------------------------------------------------------
   A BARRA DE FILTROS
   --------------------------------------------------------------------------- */
.kpi-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.kpi-period {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex: 0 0 auto;
}

/* O campo e uma caixa flex: a moldura fica no wrapper, e o input e a etiqueta
   dividem o espaco lado a lado. Era a etiqueta absoluta por cima do input, com
   um padding-right fixo reservando lugar -- e "Personalizado" nao cabia nele:
   a etiqueta cobria o fim da data. Assim a data nunca fica por baixo; no
   maximo o input corta. */
.kpi-period__field {
  display: flex;
  align-items: center;
  width: 25rem;
  height: 2.714rem;
  border: 1px solid var(--kpi-input-border, #d8d6de);
  border-radius: 0.357rem;
  background: var(--kpi-input-bg, #fff);
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.kpi-period__field:focus-within {
  border-color: #f26237;
  box-shadow: 0 3px 10px 0 rgba(34, 41, 47, 0.1);
}

.kpi-period__icon {
  flex: none;
  margin-left: 0.8rem;
  color: var(--kpi-ink-muted, #b9b9c3);
  pointer-events: none;
}

/* O input que aparece e o `altInput` que o flatpickr cria, fora do template:
   sem ::v-deep o escopo nao o alcanca. A moldura e do wrapper. */
.kpi-period__field ::v-deep .form-control {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  padding: 0 0.5rem 0 0.6rem;
  border: 0;
  background: transparent;
  box-shadow: none;
  cursor: pointer;
  font-variant-numeric: tabular-nums;
}

/* A etiqueta diz o que o periodo E ("Este mes", "Agosto 2026"), para nao
   ser preciso ler duas datas e fazer a conta. */
.kpi-period__tag {
  flex: none;
  margin-right: 0.55rem;
  padding: 0.1rem 0.55rem;
  border-radius: 1rem;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
  background: rgba(242, 98, 55, 0.12);
  color: var(--kpi-period-tag-fg, #ca390d);
  pointer-events: none;
}

.kpi-bar__pn {
  flex: 1 1 240px;
  min-width: 200px;
}

.kpi-bar__refresh {
  min-width: 132px;
}

/* ---------------------------------------------------------------------------
   A FILEIRA DE RECORTE ATIVO
   As cores passam por token porque o cinza #f3f2f7 do chip vira um bloco
   claro gritando no tema escuro.
   --------------------------------------------------------------------------- */
.kpi-chips {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.85rem;
}

.kpi-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.44rem;
  height: 28px;
  padding: 0 0.375rem 0 0.7rem;
  border-radius: 20px;
  background: var(--kpi-chip-bg, #f3f2f7);
  font-size: 0.775rem;
  font-weight: 500;
  color: var(--kpi-chip-fg, #5e5873);
  white-space: nowrap;
}

/* O nome do campo em peso menor: quem varre a fileira procura o VALOR, e o
   rotulo so existe para dizer de que campo ele e. */
.kpi-chip__k {
  color: var(--kpi-ink-muted, #b9b9c3);
  font-weight: 400;
}

.kpi-chip__x {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--kpi-ink-muted, #82868b);
  cursor: pointer;
}

.kpi-chip__x:hover {
  background: var(--kpi-chip-x-hover, #e4e2ec);
}

.kpi-chips__clear {
  padding: 0;
  border: 0;
  background: transparent;
  font-size: 0.775rem;
  color: var(--kpi-ink-muted, #b9b9c3);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.kpi-chips__clear:hover {
  color: var(--kpi-chips-clear-hover, #6e6b7b);
}

.kpi-chips__info {
  margin-left: auto;
  color: var(--kpi-ink-muted, #b9b9c3);
  cursor: help;
}

/* ---------------------------------------------------------------------------
   O RECORTE
   Tres grupos lado a lado, cada um com titulo e um filete embaixo -- a mesma
   gramatica das faixas da tabela. Nada de caixa em volta de cada grupo: com
   alturas diferentes, as caixas viravam retangulos verticais de fundo vazio.
   --------------------------------------------------------------------------- */
.kpi-recorte {
  margin-top: 1rem;
  border: 1px solid var(--kpi-line-strong, rgba(34, 41, 47, 0.08));
  border-radius: 0.5rem;
}

.kpi-recorte__grupos {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 1.75rem;
  padding: 1rem 1rem 1.15rem;
}

.kpi-recorte__grupo {
  flex: 1 1 300px;
  min-width: 0;
}

.kpi-recorte__titulo {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin: 0 0 0.85rem;
  padding-bottom: 0.55rem;
  border-bottom: 1px solid var(--kpi-line-strong, #ebe9f1);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--kpi-ink-muted, #b9b9c3);
}

.kpi-recorte__titulo--inline {
  margin: 0;
  padding: 0;
  border: 0;
}

.kpi-recorte__campos {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.kpi-recorte__campo {
  min-width: 0;
}

.kpi-recorte__campo label {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  margin-bottom: 0.3rem;
  font-size: 0.8rem;
  font-weight: 500;
}

.kpi-recorte__info {
  color: var(--kpi-ink-muted, #b9b9c3);
  cursor: help;
}

/* A linha de admin: mais escura que o painel, para ler como "outra camada". */
.kpi-recorte__avancado {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem 1.5rem;
  padding: 0.9rem 1rem;
  border-top: 1px solid var(--kpi-line-strong, rgba(34, 41, 47, 0.08));
  background: var(--kpi-surface-sunken, #fcfcfd);
}

.kpi-recorte__opcao {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.8rem;
  font-weight: 500;
}

.kpi-recorte__rodape {
  display: flex;
  justify-content: flex-end;
  padding: 0.5rem 0.75rem;
  border-top: 1px solid var(--kpi-line-strong, rgba(34, 41, 47, 0.08));
}

/* O ativo PREENCHIDO, como na regua da tabela. O !important no texto vence o
   do tema escuro (ver a regua em KpiTreeTable.vue). */
.kpi-seg ::v-deep .btn.active:not(.disabled),
.kpi-seg ::v-deep .btn:not(:disabled):not(.disabled).active {
  background-color: #f26237;
  border-color: #f26237;
  color: #fff !important;
  box-shadow: none;
}

/* A natureza do dado: quanto do período já é fotografia. */
.kpi-photo {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0 1.5rem 0.85rem;
}

.kpi-photo__bar {
  flex: 0 1 190px;
  height: 5px;
  border-radius: 3px;
  overflow: hidden;
  /* O fundo é o que ainda está vivo; a barra preenchida é o que congelou. */
  background: rgba(242, 98, 55, 0.18);
}

.kpi-photo__frozen {
  display: block;
  height: 100%;
  border-radius: 3px;
  background: #f26237;
  transition: width 320ms cubic-bezier(0.22, 1, 0.36, 1);
}

.kpi-photo__text {
  font-size: 0.786rem;
  color: var(--kpi-ink-muted, #b9b9c3);
}

@media (prefers-reduced-motion: reduce) {
  .kpi-photo__frozen {
    transition: none;
  }
}

/* ---------------------------------------------------------------------------
   Os quatro totalizadores.

   Sao <button>: clicar num deles troca o recorte da tabela, e e por isso que o
   cartao tem estado pressionado. O quarto nao tem recorte correspondente — e
   razao, nao soma — entao fica inerte, sem cursor de clique.
   --------------------------------------------------------------------------- */
.kpi-tiles {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1.25rem;
  margin-bottom: 1.5rem;
}

.kpi-tile {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem 1.15rem 1.1rem;
  text-align: left;
  background: var(--kpi-tile-bg, #fff);
  /* No claro o cartao se separa do fundo pela SOMBRA e nao precisa de borda.
     No escuro sombra de tinta escura sobre fundo escuro nao existe, entao a
     separacao passa a vir da aresta -- o token vale transparent no claro. */
  border: 1px solid var(--kpi-tile-edge, transparent);
  border-radius: 0.428rem;
  box-shadow: 0 4px 24px 0 var(--kpi-shadow, rgba(34, 41, 47, 0.1));
  transition: transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease;
}

.kpi-tile[aria-pressed] {
  cursor: pointer;
}

.kpi-tile:not([aria-pressed]) {
  cursor: default;
}

.kpi-tile:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 28px 0 var(--kpi-shadow-hover, rgba(34, 41, 47, 0.14));
}

/* O destaque do consolidado e VERDE, nao laranja: o laranja da marca ja e a
   cor de "selecionado", e usar os dois no mesmo cartao confundiria as duas
   leituras. */
.kpi-tile--strong {
  border-color: rgba(40, 199, 111, 0.35);
}

.kpi-tile--strong .kpi-tile__value {
  color: var(--kpi-tile-strong, #197b44);
}

/* O recorte ativo e uma BARRA no pe do cartao, nao um anel.
   Com anel o laranja de "selecionado" comeria a borda verde do consolidado, e
   as duas marcas dizem coisas diferentes: o verde e "este e o numero de
   cabeceira", o laranja e "e este que a tabela esta mostrando". */
.kpi-tile--on {
  position: relative;
}

.kpi-tile--on::after {
  content: '';
  position: absolute;
  right: 1.25rem;
  bottom: 0;
  left: 1.25rem;
  height: 3px;
  background: #f26237;
  border-radius: 3px 3px 0 0;
}

.kpi-tile__head {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.kpi-tile__ico {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  border-radius: 0.5rem;
  /* O tint vem por custom property inline, e nao por `background` inline, para
     que o tema escuro consiga sobrescrever: declaracao inline ganha de
     qualquer folha de estilo, custom property nao. */
  background: var(--tile-tint, transparent);
}

/* O glifo seguia um `color` inline, que ganha de qualquer folha e por isso
   tornava o icone inalcancavel pelo tema. Agora a cor vem da mesma custom
   property do bloco. */
.kpi-tile__ico-glyph {
  color: var(--tile-ink, inherit);
}

/* No escuro o pastel vira um quadrado claro gritando dentro do cartao #283046.
   O bloco perde o fundo tingido, ganha um veu neutro, e a identidade passa
   para a TINTA -- mesma decisao das faixas da tabela. */
body.dark-layout .kpi-tile__ico {
  background: var(--kpi-chip-bg, rgba(255, 255, 255, 0.06));
  color: var(--tile-ink-dark, #b4b7bd);
}

body.dark-layout .kpi-tile__ico-glyph {
  color: var(--tile-ink-dark, #b4b7bd);
}

.kpi-tile__label {
  min-width: 0;
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.25;
  color: var(--kpi-tile-fg, #5e5873);
}

/* De quem e o numero, quando nao e de quem esta olhando (filtro de PN). Fica
   no cartao, e nao so no (i): e o que impede "Producao propria" de ser lida
   como a propria. */
.kpi-tile__owner {
  display: block;
  overflow: hidden;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: var(--kpi-ink-muted, #82868b);
}

.kpi-tile__info {
  flex: 0 0 auto;
  margin-left: auto;
  color: var(--kpi-tile-muted, #b9b9c3);
  cursor: help;
}

.kpi-tile__info:hover {
  color: #f26237;
}

.kpi-tile__value {
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: var(--kpi-tile-fg, #5e5873);
}

@media (prefers-reduced-motion: reduce) {
  .kpi-tile {
    transition: none;
  }

  .kpi-tile:hover {
    transform: none;
  }
}

/* ---------------------------------------------------------------------------
   FRENTE 1 - COMPONENTES E COLUNAS
   Medidas do canvas (Personalizacao.dc.html:21-68). Cores em token: o painel
   inteiro e feito de cinzas claros, que no tema escuro seriam blocos brilhantes.
   --------------------------------------------------------------------------- */
.kpi-columns-panel {
  border: 1px solid var(--kpi-line-strong, rgba(34, 41, 47, 0.08));
  border-radius: 0.4rem;
  overflow: hidden;
}

/* Dentro da gaveta a moldura sobra: a propria gaveta ja e a borda. E o rodape
   com o contador desce para o pe, senao ele flutua no meio de uma coluna alta
   e a pergunta "quantas colunas estao ligadas" fica perdida no vao. */
.kpi-columns-panel--sidebar {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 3.5rem);
  border: 0;
  border-radius: 0;
}

.kpi-columns-panel--sidebar .kpi-columns-panel__body {
  flex: 1 1 auto;
}

.kpi-columns-panel--sidebar .kpi-columns-panel__actions {
  position: sticky;
  bottom: 0;
}

.kpi-columns-panel__header {
  padding: 1rem 1.1rem 0.9rem;
  border-bottom: 1px solid var(--kpi-line, #ebe9f1);
}

.kpi-columns-panel__desc {
  margin: 0.25rem 0 0;
  font-size: 0.76rem;
  line-height: 1.5;
  color: var(--kpi-ink-muted, #b9b9c3);
}

.kpi-presets {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.85rem;
}

.kpi-preset {
  display: inline-flex;
  align-items: center;
  gap: 0.44rem;
  height: 30px;
  padding: 0 0.75rem;
  border: 1px solid var(--kpi-line, #ebe9f1);
  border-radius: 20px;
  background: transparent;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--kpi-preset-fg, #6e6b7b);
  white-space: nowrap;
  cursor: pointer;
}

.kpi-preset:hover {
  border-color: var(--kpi-line-strong, #d8d6e0);
}

/* O preset aceso responde "de onde veio esta selecao". Sem ele, tres estados
   bem diferentes -- padrao, so faturamento e escolha manual -- ficam com a
   mesma aparencia. */
.kpi-preset--on {
  border-color: #f26237;
  background: rgba(242, 98, 55, 0.08);
  color: var(--kpi-preset-on-fg, #ca390d);
  font-weight: 600;
}

.kpi-group {
  border-bottom: 1px solid var(--kpi-line, #f2f1f6);
}

.kpi-group:last-child {
  border-bottom: none;
}

.kpi-group__row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.7rem 1.1rem;
}

.kpi-group__name {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1 1 auto;
  min-width: 0;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.kpi-group__name:hover .kpi-group__label {
  color: #f26237;
}

/* O quadradinho de cor e o que amarra este painel a faixa da tabela: e a mesma
   cor, no mesmo bloco. Quem desliga "Momento X" aqui sabe qual faixa some la. */
.kpi-group__dot {
  flex: 0 0 10px;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background: var(--group-base, transparent);
}

.kpi-group__texts {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.kpi-group__label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--kpi-group-fg, #5e5873);
}

.kpi-group__meta {
  margin-top: 0.1rem;
  font-size: 0.68rem;
  color: var(--kpi-ink-muted, #b9b9c3);
}

.kpi-group__caret {
  margin-left: auto;
  flex: 0 0 auto;
  color: var(--kpi-ink-muted, #b9b9c3);
}

.kpi-group__switch {
  flex: 0 0 auto;
  margin: 0;
  padding-left: 2.6rem;
}

.kpi-group__cols {
  display: flex;
  flex-wrap: wrap;
  gap: 0.44rem;
  padding: 0 1.1rem 0.85rem 2.2rem;
}

/* Chip, e nao checkbox empilhado: com 33 colunas a lista vertical virava uma
   parede: a forma de chip deixa o bloco inteiro caber em duas ou tres linhas. */
.kpi-cbx {
  display: inline-flex;
  align-items: center;
  gap: 0.44rem;
  height: 28px;
  padding: 0 0.7rem 0 0.5rem;
  border: 1px solid var(--kpi-line, #ebe9f1);
  border-radius: 0.36rem;
  background: transparent;
  font-size: 0.74rem;
  font-weight: 500;
  color: var(--kpi-preset-fg, #6e6b7b);
  white-space: nowrap;
  cursor: pointer;
}

.kpi-cbx:hover:not(:disabled) {
  border-color: var(--kpi-line-strong, #d8d6e0);
}

.kpi-cbx__box {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 15px;
  width: 15px;
  height: 15px;
  border: 1.5px solid var(--kpi-line-strong, #d8d6e0);
  border-radius: 3px;
  color: transparent;
}

.kpi-cbx--on {
  border-color: #f26237;
  background: rgba(242, 98, 55, 0.06);
  color: var(--kpi-group-fg, #5e5873);
}

.kpi-cbx--on .kpi-cbx__box {
  border-color: #f26237;
  background: #f26237;
  color: #fff;
}

/* A obrigatoria aparece marcada e sem clique, como no canvas. Nao leva selo:
   um rotulo "sempre visivel" ao lado de uma caixa cinza e a mesma informacao
   dita duas vezes. */
.kpi-cbx--pinned {
  background: var(--kpi-surface-sunken, #f8f8f8);
  color: var(--kpi-ink-muted, #b9b9c3);
  cursor: not-allowed;
}

.kpi-cbx--pinned .kpi-cbx__box {
  border-color: var(--kpi-line-strong, #d8d6e0);
  background: var(--kpi-ink-faint, #d8d6e0);
  color: var(--kpi-surface-sunken, #fff);
}

.kpi-columns-panel__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.8rem 1.1rem;
  border-top: 1px solid var(--kpi-line, #ebe9f1);
  background: var(--kpi-surface-sunken, #fcfcfd);
}

// '13 de 33' responde a pergunta que o painel inteiro existe para responder.
.kpi-columns-panel__count {
  font-size: 0.8rem;
  color: var(--kpi-ink-muted, #b9b9c3);
  white-space: nowrap;
}

.kpi-columns-panel__restore {
  padding: 0;
  border: 0;
  background: transparent;
  font-size: 0.78rem;
  color: var(--kpi-ink-muted, #b9b9c3);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.kpi-columns-panel__restore:hover {
  color: var(--kpi-chips-clear-hover, #6e6b7b);
}

/* ---------------------------------------------------------------------------
   Tela estreita.

   A tira de totais vira duas colunas em vez de uma fila que estoura, e os
   paineis de recorte e de componentes empilham. Nada some: o Faturometro no
   celular e consultado, nao configurado, mas quem abriu o painel quer usa-lo.
   --------------------------------------------------------------------------- */
@media (max-width: 1199.98px) {
  .kpi-tiles {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 575.98px) {
  .kpi-tiles {
    grid-template-columns: minmax(0, 1fr);
    gap: 0.75rem;
  }
}

@media (max-width: 767.98px) {
  .kpi-photo {
    padding: 0 1rem 0.75rem;
    flex-wrap: wrap;
  }

  .kpi-photo__bar {
    flex-basis: 100%;
  }

  .kpi-period,
  .kpi-period__field {
    flex: 1 1 100%;
    width: auto;
  }

  .kpi-recorte__campos {
    grid-template-columns: minmax(0, 1fr);
  }

  .kpi-chips__info {
    display: none;
  }

  .kpi-group__cols {
    padding-left: 1.1rem;
  }
}
</style>

<!-- Nao escopado: o calendario do flatpickr e pendurado no <body>. -->
<style lang="scss">
// O tema do calendario. Esta tela nunca importou o scss do flatpickr do
// template (KpiList.vue importa), e por isso o calendario abria no tema
// padrao da biblioteca -- branco com azul no meio do tema escuro -- e o campo
// readonly ficava com cara de desabilitado.
@import '@core/scss/vue/libs/vue-flatpicker.scss';

// Os atalhos de periodo. A classe .kpi-calendario, posta no onReady, limita
// isto a este calendario.
.kpi-calendario .shortcut-buttons-flatpickr-wrapper {
  display: block;
  padding: 0.65rem 0.6rem 0.75rem;
  border-top: 1px solid var(--kpi-line, #ebe9f1);
}

.kpi-calendario .shortcut-buttons-flatpickr-buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.4rem;
}

.kpi-calendario .kpi-calendario__atalho {
  height: 28px;
  margin: 0;
  padding: 0 0.75rem;
  border: 1px solid var(--kpi-line-strong, #d8d6de);
  border-radius: 14px;
  background: transparent;
  font-size: 0.8rem;
  color: inherit;
  cursor: pointer;

  &:hover {
    border-color: #f26237;
    color: #f26237;
  }

  &.is-active {
    border-color: #f26237;
    background: #f26237;
    color: #fff;
    font-weight: 600;
  }
}
</style>
